/**
 * =============================================================================
 * MULTICHAIN ERC-8004 EXPLORER - SEO OPTIMIZED
 * =============================================================================
 * /
 * Features:
 * - SEO-friendly URLs: /chain-key/agent/id
 * - React Router for client-side navigation
 * - Dynamic meta tags for social sharing
 * - Structured data (JSON-LD) for search engines
 */
import './index.css';
//import AgentPlayground from './AgentPlayground';
import { createClient } from '@supabase/supabase-js';
import CreateAgentPage from './createAgent';
import { NewsPage, ArticlePage } from './NewsPage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  Search, Star, Shield, ExternalLink, Users, CheckCircle, Loader2, Activity, Award, Globe, Filter, TrendingUp, MessageSquare, CheckSquare, BarChart3, Sparkles, Plus, ChevronLeft,
  ChevronRight,
  Clock,
  ArrowRight,
  Newspaper,
  Calendar,
  ArrowLeft,
  Tag,
  Share2,
  Twitter,
  Linkedin,
  Copy,
  User,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import PrivacyPage from './PrivacyPage';
import TermsPage from './TermsPage';
import ContactPage from './ContactPage';

//image optimization helper
const failedImageUrls = new Set();

function optimizeImage(url, width = 112) {
  if (!url || failedImageUrls.has(url)) {
    return `https://api.dicebear.com/7.x/bottts/svg?seed=default`;
  }

  // Skip optimization for SVGs, dicebear, localhost, and IPFS
  if (
    url.includes('dicebear') ||
    url.endsWith('.svg') ||
    url.includes('localhost') ||
    url.includes('ipfs.io') ||
    url.includes('ipfs://') ||
    url.includes('/ipfs/')
  ) {
    return url;
  }

  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&w=${width}&h=${width}&fit=cover&output=webp`;
}

// Simple cache to avoid re-fetching on navigation
const apiCache = {
  data: new Map(),
  maxAge: 30000, // 30 seconds

  get(key) {
    const item = this.data.get(key);
    if (!item) return null;
    if (Date.now() - item.timestamp > this.maxAge) {
      this.data.delete(key);
      return null;
    }
    return item.value;
  },

  set(key, value) {
    this.data.set(key, { value, timestamp: Date.now() });
  }
};

// =============================================================================
// SKELETON COMPONENTS - Place right after your imports, before CONFIGURATION
// =============================================================================

function StatsCardsSkeleton() {
  return (
    <div className="mb-3 md:mb-6 relative">
      {/* Live indicator placeholder */}
      <div className="absolute top-0 right-0 flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full">
        <div className="w-2 h-2 bg-gray-300 rounded-full" />
        <span className="text-xs text-gray-400">Loading...</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 animate-pulse">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-2 md:p-2.5 rounded-lg bg-gray-200 w-9 h-9 md:w-11 md:h-11" />
              <div className="flex-1 min-w-0">
                <div className="h-5 md:h-7 bg-gray-200 rounded w-12 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-16" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AgentTableSkeleton({ rows = 10 }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header skeleton */}
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="h-5 bg-gray-200 rounded w-24 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
          </div>
          <div className="flex-1 mx-8 max-w-2xl">
            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-2">
            <div className="w-16 h-8 bg-gray-200 rounded animate-pulse" />
            <div className="w-16 h-8 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>

      {/* Table header */}
      <div className="hidden md:block">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 flex">
          <div className="w-64 h-4 bg-gray-200 rounded animate-pulse" />
          <div className="w-32 h-4 bg-gray-200 rounded animate-pulse ml-6" />
          <div className="w-28 h-4 bg-gray-200 rounded animate-pulse ml-6" />
          <div className="w-28 h-4 bg-gray-200 rounded animate-pulse ml-6" />
          <div className="w-24 h-4 bg-gray-200 rounded animate-pulse ml-6" />
          <div className="w-24 h-4 bg-gray-200 rounded animate-pulse ml-6" />
        </div>
      </div>

      {/* Table rows */}
      <div className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
            {/* Avatar + Name */}
            <div className="flex items-center gap-3 w-64">
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
            {/* Chain */}
            <div className="w-32">
              <div className="h-6 bg-gray-200 rounded-full w-24" />
            </div>
            {/* Owner */}
            <div className="w-28">
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
            {/* Reputation */}
            <div className="w-28">
              <div className="h-4 bg-gray-200 rounded w-16" />
            </div>
            {/* Feedback */}
            <div className="w-24">
              <div className="h-4 bg-gray-200 rounded w-8" />
            </div>
            {/* Status */}
            <div className="w-24">
              <div className="h-6 bg-gray-200 rounded w-16" />
            </div>
          </div>
        ))}
      </div>

      {/* Pagination skeleton */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
        <div className="flex gap-2">
          <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
          <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
          <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
          <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function AgentGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-14 h-14 bg-gray-200 rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-5 bg-gray-200 rounded-full w-24" />
            </div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2" />
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
          <div className="flex items-center justify-between">
            <div className="h-4 bg-gray-200 rounded w-16" />
            <div className="h-4 bg-gray-200 rounded w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityFeedSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 bg-gray-300 rounded-full" />
          <div className="h-3 bg-gray-200 rounded w-16 animate-pulse" />
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="px-4 py-3 animate-pulse">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-gray-200 rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
              <div className="h-3 bg-gray-200 rounded w-8" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsBannerSkeleton() {
  return (
    <div className="bg-white border-b border-gray-200 py-3">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-2">
          <div className="h-5 bg-gray-200 rounded w-28 animate-pulse" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-200 rounded animate-pulse" />
            <div className="w-6 h-6 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex-1 h-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const API_KEY = process.env.REACT_APP_API_KEY || '';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://8004agents.ai';
const SITE_NAME = 'ERC-8004 Explorer';

const MAINNET_CHAINS = ['ethereum', 'base', 'polygon', 'bsc', 'monad', 'arbitrum', 'avalanche'];
const TESTNET_CHAINS = ['sepolia', 'baseSepolia', 'lineaSepolia', 'polygonAmoy', 'hederaTestnet', 'hyperevmTestnet', 'skaleBaseSepolia', 'bscTestnet'];

const CHAIN_COLORS = {
  // Mainnets
  ethereum: '#808080',
  polygon: '#8247e5',
  base: '#0052ff',
  bsc: '#F0B90B',
  monad: '#836EF9',
  //gnosis: '#04795B',
  //scroll: '#FFEEDA',
  //celo: '#FCFF52',
  arbitrum: '#28A0F0',
  avalanche: '#E84142',
  // Testnets
  sepolia: '#627eea',
  baseSepolia: '#0052ff',
  lineaSepolia: '#121212',
  polygonAmoy: '#8247e5',
  hederaTestnet: '#000000',
  hyperevmTestnet: '#00d4aa',
  skaleBaseSepolia: '#00c08b',
  bscTestnet: '#F0B90B'
};

// URL-friendly chain key mapping
const CHAIN_URL_MAP = {
  // Mainnets
  'ethereum': 'ethereum',
  'base': 'base',
  'polygon': 'polygon',
  'bsc': 'bsc',
  'monad': 'monad',
  //'gnosis': 'gnosis',
  //'scroll': 'scroll',
  //'celo': 'celo',
  'arbitrum': 'arbitrum',
  'avalanche': 'avalanche',
  // Testnets
  'sepolia': 'sepolia',
  'base-sepolia': 'baseSepolia',
  'linea-sepolia': 'lineaSepolia',
  'polygon-amoy': 'polygonAmoy',
  'hedera-testnet': 'hederaTestnet',
  'hyperevm-testnet': 'hyperevmTestnet',
  'skale-base-sepolia': 'skaleBaseSepolia',
  'bsc-testnet': 'bscTestnet'
};

const CHAIN_URL_REVERSE = Object.fromEntries(
  Object.entries(CHAIN_URL_MAP).map(([k, v]) => [v, k])
);

// =============================================================================
// API CLIENT
// =============================================================================


// Supabase fallback client (use your actual values)
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON                   // Replace with your anon/public key
);

/**
 * Helper function to make authenticated API requests
 */
async function fetchWithAuth(url, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add API key if configured
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle rate limiting
  if (response.status === 429) {
    const data = await response.json();
    throw new Error(`Rate limited: ${data.message || 'Too many requests. Please try again later.'}`);
  }

  // Handle auth errors
  if (response.status === 401 || response.status === 403) {
    const data = await response.json();
    throw new Error(`Authentication failed: ${data.error || 'Invalid or missing API key'}`);
  }

  return response;
}

const api = {
  async getChains() {
    const cached = apiCache.get('chains');
    if (cached) return cached;

    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/chains`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      apiCache.set('chains', data.chains);  // ✅ ADD THIS
      return data.chains;
    } catch (error) {
      console.warn('Backend down, using hardcoded chains fallback');
      const fallbackChains = [
        { key: 'ethereum', name: 'Ethereum', chainId: 1, isMainnet: true },
        //{ key: 'base', name: 'Base', chainId: 8453, , isMainnet: true  },
        { key: 'sepolia', name: 'Sepolia', chainId: 11155111, isMainnet: false },
        /* OLD CHAINS - keep for reference
        { key: 'baseSepolia', name: 'Base Sepolia', chainId: 84532, isMainnet: false },
        { key: 'lineaSepolia', name: 'Linea Sepolia', chainId: 59141, isMainnet: false },
        { key: 'polygonAmoy', name: 'Polygon Amoy', chainId: 80002 isMainnet: false },
        { key: 'hederaTestnet', name: 'Hedera Testnet', chainId: 296, isMainnet: false },
        { key: 'hyperevmTestnet', name: 'HyperEVM Testnet', chainId: 998,isMainnet: false  },
        { key: 'skaleBaseSepolia', name: 'SKALE Base Sepolia', chainId: 1444673419, isMainnet: false },
        { key: 'bscTestnet', name: 'BSC Testnet', chainId: 97, isMainnet: false }
        */
      ];
      apiCache.set('chains', fallbackChains);  // ✅ Cache fallback too
      return fallbackChains;
    }
  },

  async getAgents(chainFilter = null, statusFilter = null, sortBy = null, sortOrder = null, page = 1, pageSize = 10) {
    try {
      let url = `${BACKEND_API_URL}/api/agents?page=${page}&pageSize=${pageSize}`;

      if (chainFilter && chainFilter !== 'all') {
        url += `&chain=${chainFilter}`;
      }
      if (statusFilter && statusFilter !== 'all') {
        url += `&filter=${statusFilter}`;
      }
      if (sortBy) {
        url += `&sortBy=${sortBy}`;
      }
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`;
      }

      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      return {
        agents: data.agents || [],
        total: data.total || 0,
        totalPages: data.totalPages || 1
      };
    } catch (error) {
      console.warn('Backend down, using Supabase fallback');
      // Keep your existing fallback code here
      let query = supabase.from('agents').select('*', { count: 'exact' });

      if (chainFilter && chainFilter !== 'all') {
        query = query.eq('chain_key', chainFilter);
      }
      if (statusFilter === 'verified') {
        query = query.eq('verified', true);
      } else if (statusFilter === 'top-rated') {
        query = query.gte('reputation', 90);
      }

      const isAsc = sortOrder === 'asc';
      if (sortBy === 'reputation') {
        query = query.order('reputation', { ascending: isAsc });
      } else if (sortBy === 'reviewcount') {
        query = query.order('reviewcount', { ascending: isAsc });
      } else if (sortBy === 'paidinteractions') {
        query = query.order('paidinteractions', { ascending: isAsc });
      } else if (sortBy === 'created_at') {
        query = query.order('created_at', { ascending: isAsc });
      } else {
        query = query.order('last_updated', { ascending: false });
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error: sbError, count } = await query;
      if (sbError) throw sbError;

      return {
        agents: data || [],
        total: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    }
  },

  async getTrustGraph(chain, id) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/agents/${chain}/${id}/trust-graph`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.graph;
    } catch (error) {
      console.warn('Failed to fetch trust graph:', error);
      return { nodes: [], edges: [], stats: {} };
    }
  },

  async getAgent(chain, id) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/agents/${chain}/${id}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.agent;
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for agent');
      const { data, error: sbError } = await supabase
        .from('agents')
        .select('*')
        .eq('chain_key', chain)
        .eq('id', id)
        .single();
      if (sbError) throw sbError;
      return data;
    }
  },

  async getStats() {
    const cached = apiCache.get('stats');
    if (cached) return cached;

    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/stats`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      apiCache.set('stats', data);  // ✅ ADD THIS
      return data;
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for stats');
      const { data: agents, error: sbError } = await supabase
        .from('agents')
        .select('chain_key, chain_name');
      if (sbError) throw sbError;

      const chains = {};
      (agents || []).forEach(agent => {
        if (!chains[agent.chain_key]) {
          chains[agent.chain_key] = {
            name: agent.chain_name,
            inDatabase: 0,
            synced: true
          };
        }
        chains[agent.chain_key].inDatabase++;
      });

      const result = {
        total: {
          inDatabase: agents?.length || 0,
          synced: true
        },
        chains
      };
      apiCache.set('stats', result);  // ✅ Cache fallback result too
      return result;
    }
  },

  async getNewAgents() {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/stats/new-agents`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for new agents');
      try {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const { data, error: sbError } = await supabase
          .from('agents')
          .select('id', { count: 'exact' })
          .gte('created_at', oneDayAgo);
        if (sbError) throw sbError;
        return { total: data?.length || 0 };
      } catch (fallbackError) {
        console.warn('Supabase fallback also failed:', fallbackError);
        return { total: 0 };
      }
    }
  },


  async searchByAddress(address) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/search/${address}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.agents;
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for search');
      const { data, error: sbError } = await supabase
        .from('agents')
        .select('*')
        .eq('owner', address);
      if (sbError) throw sbError;
      return data || [];
    }
  },

  // ADD THIS - new text search method
  async textSearch(query) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/search/text?q=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for text search');
      try {
        const { data, error: sbError } = await supabase
          .from('agents')
          .select('*')
          .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
          .limit(50);
        if (sbError) throw sbError;
        return { success: true, agents: data || [], count: data?.length || 0 };
      } catch (fallbackError) {
        console.warn('Supabase text search fallback failed:', fallbackError);
        return { success: true, agents: [], count: 0 };
      }
    }
  },

  async semanticSearch(query) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/search/semantic`, {
        method: 'POST',
        body: JSON.stringify({ query })
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.warn('Backend down, falling back to text search for semantic query');
      // Fall back to simple text search via Supabase
      return await api.textSearch(query);
    }
  },

  async getAnalytics() {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/stats/analytics`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.analytics;
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for analytics');
      try {
        // High reputation agents
        const { data: highRep } = await supabase
          .from('agents')
          .select('*')
          .gte('reputation', 90)
          .order('reputation', { ascending: false })
          .limit(20);

        // Most reviewed agents
        const { data: mostReviewed } = await supabase
          .from('agents')
          .select('*')
          .order('reviewcount', { ascending: false })
          .limit(20);

        // Most trusted (verified with high reputation)
        const { data: mostTrusted } = await supabase
          .from('agents')
          .select('*')
          .eq('verified', true)
          .order('reputation', { ascending: false })
          .limit(20);

        return {
          highReputation: {
            count: highRep?.length || 0,
            agents: highRep || []
          },
          mostReviewed: {
            count: mostReviewed?.length || 0,
            agents: mostReviewed || []
          },
          mostTrusted: {
            count: mostTrusted?.length || 0,
            agents: (mostTrusted || []).map(a => ({
              ...a,
              trustScore: a.reputation,
              validationCount: a.validation_count || 0
            }))
          }
        };
      } catch (fallbackError) {
        console.warn('Supabase analytics fallback failed:', fallbackError);
        return null;
      }
    }
  },

  async getActivity(limit = 50, filter = 'all') {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/activity?limit=${limit}&type=${filter}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.activities || [];
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for activity');
      try {
        let query = supabase
          .from('agents')
          .select('*')
          .order('last_updated', { ascending: false })
          .limit(limit);

        const { data, error: sbError } = await query;
        if (sbError) throw sbError;

        // Transform agents into activity-like items
        return (data || []).map(agent => ({
          type: 'update',
          timestamp: agent.last_updated,
          agent: {
            id: agent.id,
            name: agent.name,
            chain_key: agent.chain_key,
            chain_name: agent.chain_name,
            image: agent.image
          },
          data: { reputation: agent.reputation }
        }));
      } catch (fallbackError) {
        console.warn('Supabase activity fallback failed:', fallbackError);
        return [];
      }
    }
  },

  async getStories(limit = 12) {
    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/stories?limit=${limit}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.success ? data.stories : [];
    } catch (error) {
      console.warn('Backend down, using Supabase fallback for stories');
      try {
        const { data, error: sbError } = await supabase
          .from('stories')
          .select('*')
          .order('published_at', { ascending: false })
          .limit(limit);
        if (sbError) throw sbError;
        return data || [];
      } catch (fallbackError) {
        // Stories table might not exist in Supabase — that's fine
        console.warn('Stories fallback failed (table may not exist):', fallbackError);
        return [];
      }
    }
  },
};
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function normalizeAgent(agent) {
  // Handle both 'services' (new) and 'endpoints' (legacy)
  let endpoints = agent.services || agent.endpoints;

  if (endpoints && !Array.isArray(endpoints) && typeof endpoints === 'object') {
    endpoints = Object.entries(endpoints).map(([key, value]) => {
      if (typeof value === 'object') {
        return { name: value.name || key, ...value };
      }
      return { name: key, endpoint: value };
    });
  }

  // Handle new decimal reputation format
  const reputationRaw = agent.reputation;
  const reputationDecimals = agent.reputation_decimals || 0;
  let reputation;

  if (reputationDecimals > 0 && typeof reputationRaw === 'number') {
    // Already converted by backend - use as-is
    reputation = reputationRaw;
  } else {
    reputation = Number(reputationRaw || 0);
  }

  return {
    ...agent,
    reputation: reputation,
    reputationDecimals: reputationDecimals,
    reviewCount: Number(agent.reviewcount || agent.reviewCount || 0),
    paidInteractions: Number(agent.paidinteractions || agent.paidInteractions || 0),
    verified: Boolean(agent.verified),
    capabilities: typeof agent.capabilities === 'string' ? JSON.parse(agent.capabilities) : (agent.capabilities || []),
    tags: typeof agent.tags === 'string' ? JSON.parse(agent.tags) : (agent.tags || []),
    endpoints: endpoints || [],
    // NEW: Handle agent_wallet from new contracts
    agentWallet: agent.agent_wallet || null,
    // Handle feedback with new value/valueDecimals format
    feedback: (agent.feedback || []).map(f => ({
      ...f,
      // New format: value + valueDecimals
      value: f.value !== undefined ? f.value : f.score,
      valueDecimals: f.valueDecimals || 0,
      valueRaw: f.valueRaw || f.value || f.score,
      // Keep score for backward compatibility
      score: f.score !== undefined ? f.score : (f.value !== undefined ? Math.max(0, Math.min(100, Math.round(f.value))) : 0),
      isRevoked: f.isRevoked || false,
      feedbackIndex: f.feedbackIndex || null
    }))
  };
}

/**
 * Format reputation for display
 * Handles decimals, negative values, and large numbers
 */
function formatReputation(value, decimals = 0, options = {}) {
  const { showMax = true, maxValue = 100 } = options;

  if (value === null || value === undefined) return '—';

  // Handle decimal display
  let displayValue;
  if (decimals > 0) {
    displayValue = value.toFixed(Math.min(decimals, 2)); // Max 2 decimal places for UI
  } else {
    displayValue = Math.round(value).toString();
  }

  // Handle negative values
  if (value < 0) {
    return displayValue; // e.g., "-1"
  }

  // Handle large values (e.g., revenues)
  if (value > 1000) {
    if (value >= 1000000) {
      displayValue = (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      displayValue = (value / 1000).toFixed(1) + 'K';
    }
    return displayValue;
  }

  // Standard reputation (0-100 scale)
  if (showMax && value <= maxValue) {
    return `${displayValue}/${maxValue}`;
  }

  return displayValue;
}

/**
 * Get color class based on reputation score
 * Updated to handle negative values and values > 100
 */
function getReputationColor(value) {
  if (value === null || value === undefined) return 'gray';
  if (value < 0) return 'red';
  if (value >= 80) return 'green';
  if (value >= 60) return 'yellow';
  if (value >= 40) return 'orange';
  return 'red';
}

function truncateAddress(address, start = 6, end = 4) {
  if (!address) return '';
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

function getChainColor(chainKey) {
  return CHAIN_COLORS[chainKey] || '#3498db';
}

function getAgentUrl(agent) {
  const urlChain = CHAIN_URL_REVERSE[agent.chain_key] || agent.chain_key;
  return `/${urlChain}/agent/${agent.id}`;
}

function getCanonicalUrl(path) {
  return `${SITE_URL}${path}`;
}

// =============================================================================
// SEO COMPONENTS
// =============================================================================

function SEOHead({ title, description, canonical, image, type = 'website', structuredData }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const defaultDescription = 'Explore ERC-8004 AI agents across Ethereum, Base, Polygon, and more blockchains. View reputation, validations, and interact with verified agents.';
  const defaultImage = `${SITE_URL}/og-image.png`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />

      <link rel="canonical" href={canonical || SITE_URL} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={image || defaultImage} />
      <meta property="og:url" content={canonical || SITE_URL} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

function AgentStructuredData({ agent }) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": agent.name,
    "description": agent.description,
    "applicationCategory": "AI Agent",
    "operatingSystem": "Blockchain",
    "aggregateRating": agent.reviewCount > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": agent.reputation,
      "bestRating": 100,
      "worstRating": 0,
      "ratingCount": agent.reviewCount
    } : undefined,
    "author": {
      "@type": "Organization",
      "name": truncateAddress(agent.owner)
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "0",
      "priceCurrency": "ETH"
    },
    "url": getCanonicalUrl(getAgentUrl(agent)),
    "image": agent.image
  };

  return structuredData;
}

// =============================================================================
// MAIN APP WITH ROUTER
// =============================================================================

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/create" element={<CreateAgentPage />} />
          <Route path="/news" element={<NewsPage Header={Header} Footer={Footer} />} />
          <Route path="/news/:slug" element={<ArticlePage Header={Header} Footer={Footer} />} />
          <Route path="/contact" element={<ContactPage Header={Header} Footer={Footer} />} />
          <Route path="/privacy" element={<PrivacyPage Header={Header} Footer={Footer} />} />
          <Route path="/terms" element={<TermsPage Header={Header} Footer={Footer} />} />
          <Route path="/:chainSlug/agent/:agentId" element={<AgentPage />} />
          <Route path="/agents" element={<HomePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}




// =============================================================================
// NEWS BANNER COMPONENT
// =============================================================================
// Add this component to your App.js file (before the HomePage function)


function NewsBanner() {
  const [stories, setStories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Fetch stories on mount
  useEffect(() => {
    async function loadStories() {
      try {
        const stories = await api.getStories(12);
        if (stories.length > 0) {
          setStories(stories);
        }
      } catch (error) {
        console.error('Failed to load news stories:', error);
      } finally {
        setLoading(false);
      }
    }
    loadStories();
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (stories.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % (stories.length - getVisibleCount() + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [stories.length, isPaused]);

  // Responsive: show different number of cards
  const getVisibleCount = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  };

  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => setVisibleCount(getVisibleCount());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, stories.length - visibleCount);

  const goToPrev = () => {
    setCurrentIndex(prev => (prev === 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getCategoryColor = (category) => {
    const colors = {
      announcement: 'bg-blue-100 text-blue-700 border-blue-200',
      ecosystem: 'bg-green-100 text-green-700 border-green-200',
      tutorial: 'bg-purple-100 text-purple-700 border-purple-200',
      analysis: 'bg-orange-100 text-orange-700 border-orange-200',
      partnership: 'bg-pink-100 text-pink-700 border-pink-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex-1 h-20 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (stories.length === 0) {
    return null;
  }

  return (
    <div
      className="bg-white border-b border-gray-200 py-3"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header - compact */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
              ERC-8004 News
            </span>
            <span className="text-gray-400 text-xs hidden sm:inline">

            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Dots indicator */}
            <div className="hidden sm:flex items-center gap-1 mr-2">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIndex ? 'bg-blue-500 w-3' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <button
              onClick={goToPrev}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNext}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* View all link */}
            <Link
              to="/news"
              className="ml-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs font-medium transition flex items-center gap-1"
            >
              All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Stories slider - compact cards */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out gap-3"
            style={{ transform: `translateX(calc(-${currentIndex} * (${100 / visibleCount}% + 0.25rem)))` }}
          >
            {stories.map((story) => (
              <Link
                key={story.id}
                to={`/news/${story.slug}`}
                className="flex-shrink-0 group"
                style={{ width: `calc(${100 / visibleCount}% - 0.75rem)` }}
              >
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 h-full hover:border-blue-300 hover:bg-blue-50/30 transition-all duration-200">
                  {/* Top row: Category & Time */}
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase border ${getCategoryColor(story.category)}`}>
                      {story.category}
                    </span>
                    <span className="text-gray-400 text-[10px]">
                      {formatDate(story.published_at)}
                    </span>
                  </div>

                  {/* Title - single line */}
                  <h4 className="text-gray-900 font-medium text-sm mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
                    {story.title}
                  </h4>

                  {/* Footer row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-gray-400 text-[10px]">
                      <Clock className="w-3 h-3" />
                      {story.read_time_minutes} min
                    </div>
                    <span className="text-blue-500 text-[10px] font-medium flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      Read <ArrowRight className="w-2.5 h-2.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// HOME PAGE
// =============================================================================

// =============================================================================
// HOME PAGE - PROGRESSIVE LOADING VERSION
// =============================================================================

function HomePage() {
  // Separate loading states for independent sections
  const [networkType, setNetworkType] = useState('mainnet'); // 'mainnet', 'testnet', or 'all'
  const [chainsLoading, setChainsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [textSearching, setTextSearching] = useState(false);

  const [chains, setChains] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [newAgents24h, setNewAgents24h] = useState(0);

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [chainFilter, setChainFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('last_updated');
  const [sortOrder, setSortOrder] = useState('desc');
  const [resultLimit, setResultLimit] = useState(10);
  const [sorting, setSorting] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [semanticSearching, setSemanticSearching] = useState(false);
  const [semanticResults, setSemanticResults] = useState(null);
  const [error, setError] = useState(null);

  const pageSize = resultLimit;
  const navigate = useNavigate();

  // Add this after the state declarations in HomePage
  const filteredChains = chains.filter(chain => {
    if (networkType === 'all') return true;

    // Prefer isMainnet flag if available
    if (chain.isMainnet !== undefined) {
      return networkType === 'mainnet' ? chain.isMainnet : !chain.isMainnet;
    }

    const isTestnet = chain.name.toLowerCase().includes('sepolia') ||
      chain.name.toLowerCase().includes('testnet') ||
      chain.name.toLowerCase().includes('amoy');
    return networkType === 'testnet' ? isTestnet : !isTestnet;
  });

  // Auto-switch to grid view on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('grid');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // CRITICAL: Load data independently (not with Promise.all)
  useEffect(() => {
    // 1. Load chains FIRST (smallest, fastest, needed for header)
    api.getChains()
      .then(data => {
        setChains(data);
        setChainsLoading(false);
      })
      .catch(err => {
        console.error('Chains error:', err);
        setChainsLoading(false);
      });

    // 2. Load stats independently
    api.getStats()
      .then(data => {
        setStats(data);
        setStatsLoading(false);
      })
      .catch(err => {
        console.error('Stats error:', err);
        setStatsLoading(false);
      });

    // 3. Load new agents count (fire and forget - lowest priority)
    api.getNewAgents()
      .then(data => setNewAgents24h(data.total || 0))
      .catch(() => setNewAgents24h(0));
  }, []); // Only run once on mount

  // Load agents when filters/pagination change
  useEffect(() => {
    if (semanticResults) return; // Skip if showing semantic results

    setAgentsLoading(true);

    // Determine effective chain filter based on networkType
    let effectiveChainFilter = chainFilter;

    if (chainFilter === 'all' && networkType !== 'all') {
      // "All Chains" selected but filtered by network type
      // Send comma-separated list of chains for that network
      effectiveChainFilter = networkType === 'mainnet'
        ? MAINNET_CHAINS.join(',')
        : TESTNET_CHAINS.join(',');
    }

    api.getAgents(
      effectiveChainFilter === 'all' ? null : effectiveChainFilter,
      filter === 'all' ? null : filter,
      sortBy,
      sortOrder,
      currentPage,
      pageSize
    )
      .then(result => {
        setAgents(result.agents.map(normalizeAgent));
        setTotalAgents(result.total);
        setTotalPages(result.totalPages);
        setAgentsLoading(false);
        setSorting(false);
      })
      .catch(err => {
        console.error('Agents error:', err);
        setError(err.message);
        setAgentsLoading(false);
        setSorting(false);
      });
  }, [chainFilter, filter, sortBy, sortOrder, currentPage, pageSize, semanticResults, networkType]); // Added networkType

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filter, chainFilter, sortBy, sortOrder]);

  // Clear semantic results when search term is cleared
  useEffect(() => {
    if (!searchTerm) {
      setSemanticResults(null);
    }
  }, [searchTerm]);

  // Handler functions
  const handleChainFilterChange = useCallback((newChain) => {
    setChainFilter(newChain);
    setCurrentPage(1);
    setSemanticResults(null);
  }, []);

  const handleSortChange = useCallback((newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setSorting(true);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  }, []);

  const handleSemanticSearch = useCallback(async (query) => {
    setSemanticSearching(true);
    setSemanticResults(null);
    try {
      const result = await api.semanticSearch(query);
      if (result.success) {
        setSemanticResults(result.agents.map(normalizeAgent));
        setTotalAgents(result.count);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Semantic search failed:', err);
    } finally {
      setSemanticSearching(false);
    }
  }, []);

  // Add this handler after handleSemanticSearch
  const handleTextSearch = useCallback(async (query) => {
    setTextSearching(true);
    setSemanticResults(null);
    try {
      const result = await api.textSearch(query);
      if (result.success) {
        setSemanticResults(result.agents.map(normalizeAgent));
        setTotalAgents(result.count);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Text search failed:', err);
    } finally {
      setTextSearching(false);
    }
  }, []);

  const handleAddressSearch = useCallback(async () => {
    if (!searchTerm.startsWith('0x') || searchTerm.length !== 42) return;

    setTextSearching(true);
    setSemanticResults(null);
    try {
      const results = await api.searchByAddress(searchTerm);
      setSemanticResults(results.map(normalizeAgent));
      setTotalAgents(results.length);
      setTotalPages(1);
    } catch (err) {
      console.error('Address search failed:', err);
    } finally {
      setTextSearching(false);
    }
  }, [searchTerm]);

  const handleAgentClick = useCallback((agent) => {
    navigate(getAgentUrl(agent));
  }, [navigate]);

  // Filter agents locally for single-word searches
  const isSemanticQuery = searchTerm.trim().includes(' ');
  const filteredAgents = (searchTerm && !isSemanticQuery)
    ? agents.filter(agent => {
      const term = searchTerm.toLowerCase();
      return agent.name.toLowerCase().includes(term) ||
        agent.description.toLowerCase().includes(term) ||
        (agent.tags || []).some(tag => tag.toLowerCase().includes(term));
    })
    : agents;

  const paginatedAgents = (searchTerm && !isSemanticQuery)
    ? filteredAgents.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filteredAgents;

  // Structured data for SEO
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": "Explore ERC-8004 AI agents across multiple blockchain networks",
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/?search={search_term_string}` },
      "query-input": "required name=search_term_string"
    }
  };

  // Show error state only for critical failures
  if (error && !agents.length && !agentsLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <SEOHead title="Error" description="Error loading agents" canonical={SITE_URL} />
        <Header
          stats={stats}
          chainFilter={chainFilter}
          onChainFilterChange={handleChainFilterChange}
          chains={chains}
          chainsLoading={chainsLoading}
          networkType={networkType}
          setNetworkType={setNetworkType}
        />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white border border-red-200 rounded-lg p-8 shadow-sm text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Data</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ALWAYS render the page structure - use skeletons for loading sections
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SEOHead
        title="ERC-8004 Explorer & News | Multichain AI Agent Tracker"
        description={`Explore ${totalAgents.toLocaleString()} ERC-8004 AI agents across ${chains.length || 8} blockchain networks.`}
        canonical={SITE_URL}
        structuredData={homeStructuredData}
      />

      {/* Header - always visible, shows skeleton for chain dropdown if loading */}
      <Header
        stats={stats}
        chainFilter={chainFilter}
        onChainFilterChange={handleChainFilterChange}
        chains={filteredChains}
        chainsLoading={chainsLoading}
        networkType={networkType}
        setNetworkType={setNetworkType}
      />

      {/* News Banner */}
      <NewsBanner />

      <main className="max-w-7xl mx-auto px-6 py-3">
        {/* Trending always render, values fill in when ready */}
        <TrendingAgents
          statsLoading={statsLoading}
          stats={stats}
          newAgents24h={newAgents24h}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main content area */}
          <div className="lg:col-span-3">
            {agentsLoading && !agents.length ? (
              // Initial load - show full skeleton
              viewMode === 'table' ? (
                <AgentTableSkeleton rows={resultLimit} />
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Loading Agents...</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                    {Array.from({ length: resultLimit }, (_, i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-14 h-14 rounded-lg bg-gray-200 animate-pulse" />
                          <div className="flex-1">
                            <h3 className="text-base font-semibold text-gray-400">Loading...</h3>
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-400">
                              Chain
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-400 mb-3">Loading agent description...</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">—/100</span>
                          <span className="text-gray-400">— uses</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ) : (
              // Data loaded - show real content (may still be refreshing)
              <AgentExplorer
                agents={semanticResults || paginatedAgents}
                filteredCount={semanticResults ? semanticResults.length : totalAgents}
                filter={filter}
                setFilter={handleFilterChange}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onAddressSearch={handleAddressSearch}
                expandedFilter={expandedFilter}
                setExpandedFilter={setExpandedFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                onSortChange={handleSortChange}
                sorting={sorting}
                resultLimit={resultLimit}
                setResultLimit={setResultLimit}
                viewMode={viewMode}
                setViewMode={setViewMode}
                onAgentClick={handleAgentClick}
                chains={chains}
                allAgents={agents}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={semanticResults ? 1 : totalPages}
                pageSize={pageSize}
                onSemanticSearch={handleSemanticSearch}
                semanticSearching={semanticSearching}
                onTextSearch={handleTextSearch}           // ADD THIS
                textSearching={textSearching}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <ActivityFeedSidebar onAgentClick={handleAgentClick} />
            </div>
          </div>
        </div>

        {/* Chain Distribution - only show when we have data */}
        {chainFilter === 'all' && stats?.chains && (
          <ChainDistribution chains={stats.chains} />
        )}
      </main>

      <Footer stats={stats} agents={agents} />
    </div>
  );
}

// =============================================================================
// AGENT PAGE (SEO-FRIENDLY URL)
// =============================================================================

function AgentPage() {
  const { chainSlug, agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert URL slug to chain key
  const chainKey = CHAIN_URL_MAP[chainSlug] || chainSlug;

  useEffect(() => {
    async function loadAgent() {
      setLoading(true);
      setError(null);
      try {
        const [chainsData, agentData] = await Promise.all([
          api.getChains(),
          api.getAgent(chainKey, agentId)
        ]);
        setChains(chainsData);
        setAgent(normalizeAgent(agentData));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadAgent();
  }, [chainKey, agentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <SEOHead
          title={`Loading Agent #${agentId}`}
          description="Loading agent details..."
          canonical={getCanonicalUrl(`/${chainSlug}/agent/${agentId}`)}
        />
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#3498db] animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-700 font-medium">Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Helmet>
          <title>Agent Not Found | ERC-8004 Explorer</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Agent Not Found</h1>
          <p className="text-gray-600 mb-6">The agent you're looking for doesn't exist or couldn't be loaded.</p>
          <Link to="/" className="px-6 py-3 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition font-medium">
            Back to Explorer
          </Link>
        </div>
      </div>
    );
  }

  const structuredData = AgentStructuredData({ agent });
  const canonicalUrl = getCanonicalUrl(getAgentUrl(agent));

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SEOHead
        title={`${agent.name} - AI Agent on ${agent.chain_name}`}
        description={`${agent.description || `ERC-8004 AI Agent #${agent.id}`}. Reputation: ${agent.reputation}/100. ${agent.reviewCount} reviews. ${agent.verified ? 'Verified agent.' : ''}`}
        canonical={canonicalUrl}
        image={agent.image}
        type="article"
        structuredData={structuredData}
      />

      <Header
        stats={null}
        chainFilter="all"
        onChainFilterChange={() => { }}
        chains={chains}
        networkType="all"
        setNetworkType={() => { }}
      />

      <main className="max-w-7xl mx-auto px-6 py-3">
        <AgentDetail
          agent={agent}
          chains={chains}
          onBack={() => navigate('/')}
        />
        {/*<AgentPlayground agent={agent} />*/}
      </main>

      <Footer stats={null} agents={[]} />
    </div>
  );
}

// =============================================================================
// 404 PAGE
// =============================================================================

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
      <Helmet>
        <title>Page Not Found | ERC-8004 Explorer</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="text-center">
        <div className="text-8xl mb-4">🤖</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
        <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
        <Link to="/" className="px-6 py-3 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition font-medium">
          Back to Explorer
        </Link>
      </div>
    </div>
  );
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

function Header({ stats, chainFilter, onChainFilterChange, chains, chainsLoading, networkType, setNetworkType }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Navigation items - simplified
  const navItems = [
    { name: 'Home', path: '/', icon: Activity },
    { name: 'News', path: '/news', icon: Newspaper },
    { name: 'Contact', path: '/contact', icon: MessageSquare },
  ];

  // Check if current path matches nav item
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main header row */}
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#3498db] to-[#2980b9] rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">ERC-8004 Explorer</h1>
              <p className="text-xs text-gray-500">Multichain ERC-8004 Explorer & News</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${active
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-blue-500' : 'text-gray-400'}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {stats && !stats.total?.synced && (
              <div className="hidden sm:block px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded text-xs font-medium border border-yellow-200">
                Syncing {stats.total?.inDatabase}/{stats.total?.onChain}
              </div>
            )}

            {/* MAINNET TESTNET TOGGLE  */}
            <div className="hidden sm:flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setNetworkType('mainnet')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition ${networkType === 'mainnet'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Mainnet
              </button>
              <button
                onClick={() => setNetworkType('testnet')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition ${networkType === 'testnet'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Testnet
              </button>
            </div>

            {/* Chain selector */}
            <div className="relative flex-shrink min-w-0 hidden sm:block">
              {chainsLoading ? (
                <div className="w-[120px] sm:w-[150px] h-10 bg-gray-100 rounded-lg animate-pulse" />
              ) : (
                <>
                  <select
                    value={chainFilter}
                    onChange={(e) => onChainFilterChange(e.target.value)}
                    className="w-full px-2 sm:px-4 py-2 pr-7 sm:pr-8 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium bg-white hover:bg-gray-50 cursor-pointer appearance-none focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db] max-w-[120px] sm:max-w-[150px]"
                  >
                    <option value="all">All Chains</option>
                    {chains.map(chain => (
                      <option key={chain.key} value={chain.key}>
                        {chain.name}
                      </option>
                    ))}
                  </select>
                  <Globe className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400 pointer-events-none" />
                </>
              )}
            </div>

            {/* Create button */}
            <Link
              to="/create"
              className="hidden sm:flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition font-medium text-xs sm:text-sm shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Create</span>
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${active
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-blue-500' : 'text-gray-400'}`} />
                  {item.name}
                </Link>
              );
            })}

            {/* Mobile-only items */}
            <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">

              {/* Network Toggle for Mobile - ADDED THIS */}
              <div className="px-4">
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Network</label>
                <div className="flex bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setNetworkType('mainnet')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${networkType === 'mainnet'
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-600'
                      }`}
                  >
                    Mainnet
                  </button>
                  <button
                    onClick={() => setNetworkType('testnet')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition ${networkType === 'testnet'
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-600'
                      }`}
                  >
                    Testnet
                  </button>
                </div>
              </div>

              {/* Chain selector for mobile */}
              <div className="px-4">
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Select Chain</label>
                {chainsLoading ? (
                  <div className="w-full h-10 bg-gray-100 rounded-lg animate-pulse" />
                ) : (
                  <div className="relative">
                    <select
                      value={chainFilter}
                      onChange={(e) => {
                        onChainFilterChange(e.target.value);
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-2.5 pr-8 border border-gray-300 rounded-lg text-sm font-medium bg-white appearance-none focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db]"
                    >
                      <option value="all">All Chains</option>
                      {chains.map(chain => (
                        <option key={chain.key} value={chain.key}>
                          {chain.name}
                        </option>
                      ))}
                    </select>
                    <Globe className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                )}
              </div>

              {/* Create button for mobile */}
              <div className="px-4">
                <Link
                  to="/create"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition font-medium text-sm shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create Agent
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function StatsCards({ agentsCount, avgReputation, newAgents24h, verifiedCount, loading }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [maxIndex, setMaxIndex] = useState(3);
  const containerRef = useRef(null);
  const totalCards = 4;
  const cardWidth = 140;
  const gap = 12;

  // Check if mobile on mount and resize, calculate max scroll index
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (mobile && containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const totalContentWidth = (cardWidth * totalCards) + (gap * (totalCards - 1));
        const maxScrollDistance = totalContentWidth - containerWidth;
        const calculatedMaxIndex = Math.ceil(maxScrollDistance / (cardWidth + gap));
        setMaxIndex(Math.max(0, Math.min(calculatedMaxIndex, totalCards - 1)));
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-scroll every 5 seconds on mobile
  useEffect(() => {
    if (!isMobile || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % (maxIndex + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [isMobile, isPaused, maxIndex]);

  // Handle dot click
  const handleDotClick = (idx) => {
    setCurrentIndex(idx);
  };

  // Calculate translate amount - cap it so last cards stay visible
  const getTranslateX = () => {
    if (!containerRef.current) return 0;

    const containerWidth = containerRef.current.offsetWidth;
    const totalContentWidth = (cardWidth * totalCards) + (gap * (totalCards - 1));
    const maxTranslate = totalContentWidth - containerWidth;
    const desiredTranslate = currentIndex * (cardWidth + gap);

    return Math.min(desiredTranslate, maxTranslate);
  };

  // Show placeholder or real value
  const displayValue = (value, fallback = "—") => {
    if (loading) return fallback;
    if (value === undefined || value === null) return fallback;
    return typeof value === 'number' ? value.toLocaleString('en-US') : value;
  };

  const stats = [
    { icon: Users, label: "Total Agents", value: displayValue(agentsCount), color: "blue" },
    //{ icon: Award, label: "Avg Reputation", value: displayValue(avgReputation), color: "purple" },
    { icon: Sparkles, label: "New (24h)", value: displayValue(newAgents24h), color: "green" },
    { icon: Shield, label: "Verified", value: displayValue(verifiedCount), color: "teal" },
  ];

  // Only show dots for positions that actually scroll
  const visibleDots = maxIndex + 1;

  return (
    <div className="mb-3 md:mb-6 relative">
      <div className="absolute top-0 right-0 flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-full z-10">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span className="text-xs text-green-700 font-medium">{loading ? 'Loading' : 'Live'}</span>
      </div>

      {/* Mobile: Transform-based carousel | Desktop: Grid */}
      {isMobile ? (
        <div
          ref={containerRef}
          className="overflow-hidden"
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setTimeout(() => setIsPaused(false), 3000)}
        >
          <div
            className="flex gap-3 transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${getTranslateX()}px)` }}
          >
            {stats.map((stat, idx) => (
              <StatCard key={idx} icon={stat.icon} label={stat.label} value={stat.value} color={stat.color} />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          {stats.map((stat, idx) => (
            <StatCard key={idx} icon={stat.icon} label={stat.label} value={stat.value} color={stat.color} isDesktop />
          ))}
        </div>
      )}

      {/* Mobile scroll indicators */}
      {isMobile && (
        <div className="flex justify-center gap-2 mt-2">
          {Array.from({ length: visibleDots }, (_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleDotClick(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === idx
                ? 'w-5 bg-blue-500'
                : 'w-1.5 bg-gray-300 active:bg-gray-400'
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TrendingAgents({ statsLoading, stats, newAgents24h, networkType }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('highRep');
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function loadAnalytics() {
      setLoading(true);
      try {
        const result = await api.getAnalytics();
        if (result && isMounted) {
          setAnalytics(result);
        }
      } catch (error) {
        console.error('Failed to load analytics:', error);
      }
      if (isMounted) {
        setLoading(false);
      }
    }
    loadAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  // Reset expanded state when network changes
  useEffect(() => {
    setIsExpanded(false);
  }, [networkType]);

  const handleAgentClick = useCallback((agent) => {
    if (agent) {
      const urlChain = CHAIN_URL_REVERSE[agent.chain_key] || agent.chain_key;
      navigate(`/${urlChain}/agent/${agent.id}`);
    }
  }, [navigate]);

  const tabs = [
    { key: 'highRep', label: 'Top Rated', icon: Star, color: 'yellow' },
    { key: 'mostReviewed', label: 'Most Reviewed', icon: MessageSquare, color: 'blue' },
    //{ key: 'mostTrusted', label: 'Most Trusted', icon: Shield, color: 'green' },
  ];

  const getAgentsForTab = () => {
    if (!analytics) return [];

    const isQualifiedAgent = (agent) => {
      const meta = agent.metadata_json;
      if (!meta || typeof meta !== 'object') return false;

      const hasX402 = meta.x402Support === true || meta.x402support === true;

      let hasEndpoint = false;
      const services = agent.endpoints || meta.services || meta.endpoints;

      if (Array.isArray(services) && services.length > 0) {
        hasEndpoint = services.some(s => {
          if (typeof s === 'string') return s.length > 0;
          if (typeof s === 'object') return !!(s.endpoint || s.url);
          return false;
        });
      } else if (typeof services === 'object' && services !== null) {
        hasEndpoint = Object.values(services).some(s => {
          if (typeof s === 'string') return s.length > 0;
          if (typeof s === 'object') return !!(s.endpoint || s.url);
          return false;
        });
      }

      return hasX402 && hasEndpoint;
    };

    let agents;
    switch (activeTab) {

      case 'highRep':
        agents = analytics.highReputation?.agents || [];
        return [...agents].filter(isQualifiedAgent).sort((a, b) => {
          if (b.reputation !== a.reputation) {
            return b.reputation - a.reputation;
          }
          return (b.reviewcount || 0) - (a.reviewcount || 0);
        });
      case 'mostReviewed':
        agents = analytics.mostReviewed?.agents || [];
        return [...agents].filter(isQualifiedAgent);
      case 'mostTrusted':
        agents = analytics.mostTrusted?.agents || [];
        return [...agents].filter(isQualifiedAgent);
      default:
        return [];
    }
  };

  const getScoreForAgent = (agent) => {
    switch (activeTab) {
      case 'highRep':
        return { value: Math.min(agent.reputation, 100), label: '/100', icon: Star, color: 'text-yellow-500' };
      case 'mostReviewed':
        return { value: agent.reviewcount, label: 'reviews', icon: MessageSquare, color: 'text-blue-500' };
      case 'mostTrusted':
        return { value: agent.trustScore, label: `(${agent.validationCount}v)`, icon: Shield, color: 'text-green-500' };
      default:
        return { value: 0, label: '', icon: Star, color: 'text-gray-500' };
    }
  };

  const allAgentsForTab = getAgentsForTab();

  // Filter by network type
  const agents = allAgentsForTab.filter(agent => {
    if (networkType === 'all') return true;

    const isTestnet = TESTNET_CHAINS.includes(agent.chain_key);
    return networkType === 'testnet' ? isTestnet : !isTestnet;
  });

  const displayCount = isExpanded ? 20 : 6;
  const displayedAgents = agents.slice(0, displayCount);

  if (loading || statsLoading) {
    return (
      <div className="mb-3 md:mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-40 animate-pulse" />
            <div className="flex gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-8 bg-gray-200 rounded w-24 animate-pulse" />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 animate-pulse">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 md:mb-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Trending Agents</h2>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-[10px] text-green-700 font-medium">Live</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs">
            </div>
          </div>

          <div className="flex gap-1 mt-3">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isActive
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : tab.color === 'yellow' ? 'text-yellow-500' : tab.color === 'blue' ? 'text-blue-500' : 'text-green-500'}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-3">
          {displayedAgents.length > 0 ? (
            <>
              <div className={`grid gap-2 ${isExpanded
                ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
                }`}>
                {displayedAgents.map((agent, idx) => {
                  const score = getScoreForAgent(agent);
                  return (
                    <TrendingAgentCard
                      key={`${agent.chain_key}-${agent.id}`}
                      agent={agent}
                      rank={idx + 1}
                      score={score}
                      onClick={() => handleAgentClick(agent)}
                    />
                  );
                })}
              </div>

              {agents.length > 6 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-full mt-3 py-2 flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show {Math.min(agents.length - 6, 14)} more agents
                    </>
                  )}
                </button>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No trending agents available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendingAgentCard({ agent, rank, score, onClick }) {
  const [imgSrc, setImgSrc] = useState(() => optimizeImage(agent.image, 40));
  const ScoreIcon = score.icon;

  const handleImgError = useCallback(() => {
    failedImageUrls.add(agent.image);
    setImgSrc(`https://api.dicebear.com/9.x/bottts/svg?seed=${agent.chain_key}${agent.id}`);
  }, [agent.image, agent.chain_key, agent.id]);

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (rank === 2) return 'bg-gray-100 text-gray-600 border-gray-300';
    if (rank === 3) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-gray-50 text-gray-500 border-gray-200';
  };

  return (
    <div
      onClick={onClick}
      className="group bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg p-2.5 cursor-pointer transition-all"
    >
      <div className="flex items-start gap-2">
        <div className={`w-5 h-5 flex-shrink-0 rounded text-[10px] font-bold flex items-center justify-center border ${getRankStyle(rank)}`}>
          {rank}
        </div>

        <img
          src={imgSrc}
          alt={agent.name}
          loading="lazy"
          decoding="async"
          className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex-shrink-0"
          onError={handleImgError}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-gray-900 truncate group-hover:text-blue-600 transition">
              {agent.name.length > 12 ? `${agent.name.slice(0, 12)}...` : agent.name}
            </span>
            {agent.metadata_json?.x402Support && (
              <span className="px-1 py-0.5 bg-green-100 text-green-700 text-[8px] font-bold rounded border border-green-200 flex-shrink-0">
                x402
              </span>
            )}
          </div>

          <div
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium text-white mt-0.5"
            style={{ backgroundColor: CHAIN_COLORS[agent.chain_key] || '#3498db' }}
          >
            {agent.chain_name?.length > 10 ? `${agent.chain_name.slice(0, 10)}...` : agent.chain_name}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-gray-200">
        <ScoreIcon className={`w-3 h-3 ${score.color}`} />
        <span className="text-xs font-bold text-gray-900">{score.value}</span>
        <span className="text-[10px] text-gray-500">{score.label}</span>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, isDesktop }) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    teal: 'from-teal-500 to-teal-600'
  };

  return (
    <div className={`${isDesktop ? 'w-auto' : 'flex-shrink-0 w-[140px]'} bg-white rounded-lg border border-gray-200 p-3 md:p-4`}>
      <div className="flex items-center gap-2 md:gap-3">
        <div className={`p-2 md:p-2.5 rounded-lg bg-gradient-to-br ${colors[color]}`}>
          <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-lg md:text-2xl font-bold text-gray-900 truncate">{value}</div>
          <div className="text-[10px] md:text-xs text-gray-600 truncate">{label}</div>
        </div>
      </div>
    </div>
  );
}

function AgentExplorer({
  agents, filteredCount, filter, setFilter, searchTerm, setSearchTerm,
  onAddressSearch, expandedFilter, setExpandedFilter, sortBy, setSortBy,
  sortOrder, setSortOrder, onSortChange, sorting, resultLimit, setResultLimit,
  viewMode, setViewMode, onAgentClick, chains, allAgents, currentPage,
  setCurrentPage, totalPages, pageSize, onSemanticSearch, semanticSearching, onTextSearch, textSearching
}) {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const inputRef = useRef(null);

  const placeholders = [

    "0x3754e2d752fe2f5a96f8f40791552fda222a00bc",
    "DeFi yield optimizer...",
    "NFT trading agent...",
    "Autonomous portfolio manager...",
    "Real-time market analyzer...",
  ];

  const MAX_QUERY_LENGTH = 55;


  // Check if query is semantic (has spaces)
  const isSemanticQuery = searchTerm.trim().includes(' ');

  // Typing animation effect
  useEffect(() => {
    if (searchTerm) {
      setDisplayedPlaceholder('');
      return;
    }

    const currentPlaceholder = placeholders[placeholderIndex];
    let charIndex = 0;
    setIsTyping(true);
    setDisplayedPlaceholder('');

    // Typing phase
    const typeInterval = setInterval(() => {
      if (charIndex < currentPlaceholder.length) {
        setDisplayedPlaceholder(currentPlaceholder.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);

        // Pause then move to next placeholder
        setTimeout(() => {
          setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
        }, 2000);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, [placeholderIndex, searchTerm]);

  const handleSearchSubmit = () => {
    if (!searchTerm.trim()) return;

    // Address search
    if (searchTerm.startsWith('0x') && searchTerm.length === 42) {
      onAddressSearch();
      return;
    }

    // Check if semantic (has spaces) or simple text search
    const isSemanticQuery = searchTerm.trim().includes(' ');

    if (isSemanticQuery && onSemanticSearch) {
      // Multi-word: use semantic/AI search
      onSemanticSearch(searchTerm.trim());
    } else if (onTextSearch) {
      // Single word: use simple text search
      onTextSearch(searchTerm.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* INSERT HERE - Agent header with loading indicator */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {filter === 'all' ? '' : filter === 'verified' ? 'Verified Agents' : 'Agents'}
        </h2>
        {sorting && (
          <div className="flex items-center gap-1 text-xs text-blue-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Updating...</span>
          </div>
        )}
      </div>
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        {/* Mobile Layout */}
        <div className="sm:hidden space-y-2">
          {/* Row 1: Title + Count */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">
                {filter === 'all' ? 'Agents' : filter === 'verified' ? 'Verified' : filter === 'top-rated' ? 'Top Rated' : 'Agents'}
              </h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                {/*
                {filteredCount.toLocaleString('en-US')}
                */}
              </span>
            </div>
            {!searchTerm && (
              <button
                onClick={() => setExpandedFilter(expandedFilter ? null : 'overview')}
                className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition flex items-center gap-1"
              >
                <Filter className="w-3 h-3" />
                Filters
              </button>
            )}
          </div>

          {/* Row 2: Search with animated placeholder */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder=""
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.slice(0, MAX_QUERY_LENGTH))}
              onKeyPress={handleKeyPress}
              className="w-full pl-8 pr-20 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db]"
            />
            {/* Animated placeholder */}
            {!searchTerm && (
              <div className="absolute left-8 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <span className="text-xs text-gray-400">
                  {displayedPlaceholder}
                  {isTyping && <span className="animate-pulse">|</span>}
                </span>
              </div>
            )}
            {/* Character count */}
            {searchTerm && (
              <span className="absolute right-20 top-1/2 transform -translate-y-1/2 text-[10px] text-gray-400">
                {searchTerm.length}/{MAX_QUERY_LENGTH}
              </span>
            )}
            {/* Submit button - pulsing blue when has content */}
            {searchTerm ? (
              <button
                onClick={handleSearchSubmit}
                disabled={semanticSearching || textSearching}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition flex items-center gap-1 animate-pulse disabled:animate-none disabled:opacity-70"
              >
                {(semanticSearching || textSearching) ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Search className="w-3 h-3" />
                )}
                Search
              </button>
            ) : (
              <button
                onClick={() => setExpandedFilter(expandedFilter ? null : 'overview')}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition flex items-center gap-1"
              >
                <Filter className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Search type indicator */}
          {searchTerm && (
            <div className="flex items-center gap-1 text-[10px] text-blue-600">
              {isSemanticQuery ? (
                <>
                  <Sparkles className="w-3 h-3" />
                  AI-powered semantic search
                </>
              ) : (
                <>
                  <Search className="w-3 h-3" />
                  Text search - press Enter or click Search
                </>
              )}
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:block">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {filter === 'all' ? 'All Agents' : filter === 'verified' ? 'Verified Agents' : filter === 'top-rated' ? 'Top Rated Agents' : 'Agents'}
              </h2>
              {/*
              <p className="text-sm text-gray-500">{filteredCount.toLocaleString('en-US')} agents found</p>
              */}
            </div>

            <div className="flex-1 mx-8 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder=""
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.slice(0, MAX_QUERY_LENGTH))}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-9 pr-36 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db]"
                />
                {/* Animated placeholder */}
                {!searchTerm && (
                  <div className="absolute left-9 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <span className="text-sm text-gray-400">
                      {displayedPlaceholder}
                      {isTyping && <span className="animate-pulse">|</span>}
                    </span>
                  </div>
                )}
                {/* Character count */}
                {searchTerm && (
                  <span className="absolute right-36 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                    {searchTerm.length}/{MAX_QUERY_LENGTH}
                  </span>
                )}
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                  {searchTerm ? (
                    <button
                      onClick={handleSearchSubmit}
                      disabled={semanticSearching || textSearching}
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition flex items-center gap-1 animate-pulse disabled:animate-none disabled:opacity-70"
                    >
                      {(semanticSearching || textSearching) ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          {isSemanticQuery ? <Sparkles className="w-3 h-3" /> : <Search className="w-3 h-3" />}
                          {isSemanticQuery ? 'AI Search' : 'Search'}
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => setExpandedFilter(expandedFilter ? null : 'overview')}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition flex items-center gap-1"
                    >
                      <Filter className="w-3 h-3" />
                      Filters
                    </button>
                  )}
                </div>
              </div>
              {/* Search type indicator */}
              {searchTerm && (
                <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                  {isSemanticQuery ? (
                    <>
                      <Sparkles className="w-3 h-3" />
                      AI-powered semantic search - press Enter or click Search
                    </>
                  ) : (
                    <>
                      <Search className="w-3 h-3" />
                      Text search - press Enter or click Search
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded text-sm ${viewMode === 'table' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}>
                Table
              </button>
              <button onClick={() => setViewMode('grid')} className={`px-3 py-1.5 rounded text-sm ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}>
                Grid
              </button>
            </div>
          </div>
        </div>

        {expandedFilter && (
          <FilterPanel
            expandedFilter={expandedFilter}
            setExpandedFilter={setExpandedFilter}
            filter={filter}
            setFilter={setFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            resultLimit={resultLimit}
            setResultLimit={setResultLimit}
            allAgents={allAgents}
            onSortChange={onSortChange}
          />
        )}
      </div>

      <div className={`relative ${sorting ? 'opacity-60' : ''} transition-opacity`}>
        {sorting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="bg-white/80 px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}

        {agents.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🤖</div>
            <p className="text-lg text-gray-700 font-medium">No agents found</p>
            <p className="text-gray-500 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : viewMode === 'table' ? (
          <AgentTable agents={agents} onSelect={onAgentClick} chains={chains} sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} onSortChange={onSortChange} sorting={sorting} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            {agents.map((agent) => (
              <AgentCard key={`${agent.chain_key}-${agent.id}`} agent={agent} onClick={() => onAgentClick(agent)} chains={chains} />
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          filteredCount={filteredCount}
          resultLimit={resultLimit}
          setResultLimit={setResultLimit}
        />
      )}
    </div>
  );
}

function AnalyticsPanel({ onFilterChange, onStatusFilter }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function loadAnalytics() {
      setLoading(true);
      try {
        const result = await api.getAnalytics();
        if (result && isMounted) {
          setAnalytics(result);
        }
      } catch (error) {
        console.error('Failed to load analytics:', error);
      }
      if (isMounted) {
        setLoading(false);
      }
    }
    loadAnalytics();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only load once

  const handleAgentClick = useCallback((agent) => {
    if (agent) {
      const urlChain = CHAIN_URL_REVERSE[agent.chain_key] || agent.chain_key;
      navigate(`/${urlChain}/agent/${agent.id}`);
    }
  }, [navigate]);

  const toggleExpand = useCallback((cardName) => {
    setExpandedCard(prev => prev === cardName ? null : cardName);
  }, []);

  if (loading) {
    return (
      <div className="p-3 bg-gray-50 rounded">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white p-3 rounded border border-gray-200 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-10 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-3 bg-gray-50 rounded text-center text-gray-500">
        Failed to load analytics
      </div>
    );
  }

  return (
    <div className="p-3 bg-gray-50 rounded">
      <div className={`grid gap-3 ${expandedCard ? 'grid-cols-1' : 'grid-cols-3'}`}>
        {/* High Reputation */}
        {(!expandedCard || expandedCard === 'highRep') && (
          <AnalyticsCard
            cardKey="highRep"
            title="HIGH REPUTATION (90+)"
            count={analytics.highReputation.count}
            subtitle="agents"
            agents={analytics.highReputation.agents}
            isExpanded={expandedCard === 'highRep'}
            onToggle={() => toggleExpand('highRep')}
            onAgentClick={handleAgentClick}
            renderScore={(agent) => (
              <div className="flex items-center gap-1 justify-end">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                <span className="font-semibold text-gray-900">{agent.reputation}</span>
                <span className="text-gray-500 text-xs">/100</span>
              </div>
            )}
          />
        )}

        {/* Most Reviewed */}
        {(!expandedCard || expandedCard === 'mostReviewed') && (
          <AnalyticsCard
            cardKey="mostReviewed"
            title="MOST REVIEWED"
            count={analytics.mostReviewed.agents[0]?.reviewcount || 0}
            subtitle="top reviews"
            agents={analytics.mostReviewed.agents}
            isExpanded={expandedCard === 'mostReviewed'}
            onToggle={() => toggleExpand('mostReviewed')}
            onAgentClick={handleAgentClick}
            renderScore={(agent) => (
              <div className="flex items-center gap-1 justify-end">
                <MessageSquare className="w-3 h-3 text-blue-500" />
                <span className="font-semibold text-gray-900">{agent.reviewcount}</span>
              </div>
            )}
          />
        )}

        {/* Most Trusted */}
        {(!expandedCard || expandedCard === 'mostTrusted') && (
          <AnalyticsCard
            cardKey="mostTrusted"
            title="MOST TRUSTED"
            count={analytics.mostTrusted.count}
            subtitle="agents"
            agents={analytics.mostTrusted.agents}
            isExpanded={expandedCard === 'mostTrusted'}
            onToggle={() => toggleExpand('mostTrusted')}
            onAgentClick={handleAgentClick}
            renderScore={(agent) => (
              <div className="flex items-center gap-1 justify-end">
                <Shield className="w-3 h-3 text-green-500" />
                <span className="font-semibold text-gray-900">{agent.trustScore}</span>
                <span className="text-gray-400 text-xs">({agent.validationCount}v)</span>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
}

// Separate memoized component to prevent re-renders
const AnalyticsCard = React.memo(function AnalyticsCard({
  title,
  count,
  subtitle,
  agents,
  cardKey,
  isExpanded,
  onToggle,
  onAgentClick,
  renderScore
}) {
  return (
    <div className={`bg-white rounded border border-gray-200 overflow-hidden transition-all ${isExpanded ? 'col-span-3' : ''}`}>
      <div
        className="p-3 cursor-pointer hover:bg-gray-50 transition"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 mb-1">{title}</div>
            <div className="text-xl font-bold text-gray-900">{count}</div>
            <div className="text-xs text-gray-500">{subtitle}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#3498db]">
              {isExpanded ? 'Collapse' : 'View all'}
            </span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && agents.length > 0 && (
        <div className="border-t border-gray-200 max-h-80 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Rank</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Agent</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Chain</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((agent, idx) => (
                <AgentRow
                  key={`${agent.chain_key}-${agent.id}`}
                  agent={agent}
                  rank={idx}
                  onClick={() => onAgentClick(agent)}
                  renderScore={renderScore}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

// Separate memoized row component
const AgentRow = React.memo(function AgentRow({ agent, rank, onClick, renderScore }) {
  const [imgSrc, setImgSrc] = useState(() => optimizeImage(agent.image, 24));

  const handleImgError = useCallback(() => {
    failedImageUrls.add(agent.image);
    setImgSrc(`https://api.dicebear.com/9.x/bottts/svg?seed=${agent.chain_key}${agent.id}`);
  }, [agent.image, agent.chain_key, agent.id]);

  return (
    <tr
      className="hover:bg-blue-50 cursor-pointer transition"
      onClick={onClick}
    >
      <td className="px-3 py-2">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${rank === 0 ? 'bg-yellow-100 text-yellow-700' :
          rank === 1 ? 'bg-gray-200 text-gray-700' :
            rank === 2 ? 'bg-orange-100 text-orange-700' :
              'bg-gray-100 text-gray-600'
          }`}>
          {rank + 1}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <img
            src={imgSrc}
            alt={agent.name}
            loading="lazy"
            decoding="async"
            width={24}
            height={24}
            className="w-6 h-6 rounded bg-gray-100"
            onError={handleImgError}
          />
          <span className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
            {agent.name}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-white"
          style={{ backgroundColor: CHAIN_COLORS[agent.chain_key] || '#3498db' }}
        >
          {agent.chain_name}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {renderScore(agent)}
      </td>
    </tr>
  );
});

function FilterPanel({ expandedFilter, setExpandedFilter, filter, setFilter, sortBy, setSortBy, sortOrder, setSortOrder, resultLimit, setResultLimit, allAgents, onSortChange }) {
  return (
    <div className="pt-4 border-t border-gray-200">
      <div className="flex gap-2 mb-4">
        {['overview', 'ID', 'validations'].map(tab => (
          <button
            key={tab}
            onClick={() => setExpandedFilter(tab)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${expandedFilter === tab ? 'bg-[#3498db] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {expandedFilter === 'overview' && (
        <div className="p-3 bg-gray-50 rounded">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded text-sm font-medium transition ${filter === 'all' ? 'bg-[#3498db] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              All Agents
            </button>
            <button onClick={() => setFilter('verified')} className={`px-3 py-1.5 rounded text-sm font-medium transition ${filter === 'verified' ? 'bg-[#3498db] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              ✓ Verified
            </button>
            <button onClick={() => setFilter('top-rated')} className={`px-3 py-1.5 rounded text-sm font-medium transition ${filter === 'top-rated' ? 'bg-[#3498db] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              ⭐ Top Rated (90+)
            </button>
          </div>
        </div>
      )}

      {expandedFilter === 'ID' && (
        <div className="p-3 bg-gray-50 rounded">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:border-[#3498db]">
                <option value="created_at">Newest First</option>
                <option value="last_updated">Recently Updated</option>
                <option value="id">Agent ID</option>
                <option value="reviewcount">Total Feedback</option>
                <option value="reputation">Reputation Score</option>
                <option value="paidinteractions">Total Interactions</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Order</label>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:border-[#3498db]">
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Per Page</label>
              <select value={resultLimit} onChange={(e) => setResultLimit(Number(e.target.value))} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:border-[#3498db]">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={() => { setSortBy('created_at'); setSortOrder('desc'); setResultLimit(10); }} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 underline">
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {expandedFilter === 'validations' && (
        <div className="p-3 bg-gray-50 rounded">
          <button
            onClick={() => setFilter('verified')}  // Changed from 'validations' to 'verified'
            className={`px-3 py-1.5 rounded text-sm font-medium transition ${filter === 'verified'
              ? 'bg-[#3498db] text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            Show Verified Only
          </button>
        </div>
      )}

    </div>
  );
}

function Pagination({ currentPage, setCurrentPage, totalPages, pageSize, filteredCount, resultLimit, setResultLimit }) {
  return (
    <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
      {/* Mobile: Stack vertically */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Info and per-page selector */}
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredCount)} of {filteredCount.toLocaleString('en-US')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Show:</span>
            <select
              value={resultLimit}
              onChange={(e) => setResultLimit(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm bg-white focus:outline-none focus:border-[#3498db]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 sm:px-3 py-1.5 border border-gray-300 rounded text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">←</span>
          </button>

          <div className="flex gap-1">
            {/* Show fewer page buttons on mobile */}
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i + 1;
              } else if (currentPage === 1) {
                pageNum = i + 1;
              } else if (currentPage === totalPages) {
                pageNum = totalPages - 2 + i;
              } else {
                pageNum = currentPage - 1 + i;
              }

              if (pageNum < 1 || pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 sm:px-3 sm:py-1.5 sm:w-auto rounded text-xs sm:text-sm ${currentPage === pageNum ? 'bg-[#3498db] text-white' : 'border border-gray-300 hover:bg-gray-50'}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 sm:px-3 py-1.5 border border-gray-300 rounded text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">Next</span>
            <span className="sm:hidden">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ChainBadge({ chainKey, chainName }) {
  const color = getChainColor(chainKey);
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium text-white whitespace-nowrap" style={{ backgroundColor: color }}>
      <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
      {chainName}
    </span>
  );
}

function ChainDistribution({ chains }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalAgents = Object.values(chains).reduce((sum, chain) => sum + chain.inDatabase, 0);
  const chainCount = Object.keys(chains).length;

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 cursor-pointer hover:bg-gray-50 transition" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#3498db]" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Chain Distribution</h3>
              <p className="text-xs text-gray-500 mt-0.5">{totalAgents.toLocaleString()} agents across {chainCount} chains</p>
            </div>
          </div>
          <button className="p-1 hover:bg-gray-200 rounded transition">
            <svg className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(chains).map(([key, chainStats]) => (
              <div key={key} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getChainColor(key) }}></div>
                    <span className="text-sm font-semibold text-gray-900">{chainStats.name}</span>
                  </div>
                  {chainStats.synced ? (
                    <span className="text-xs text-green-600 font-medium">✓ Synced</span>
                  ) : (
                    <span className="text-xs text-yellow-600 font-medium">Syncing...</span>
                  )}
                </div>
                <div className="text-2xl font-bold text-gray-900">{chainStats.inDatabase}</div>
                <div className="text-xs text-gray-500">agents indexed</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityFeedSidebar({ onAgentClick }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [limit, setLimit] = useState(10);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    loadActivities();
    const interval = setInterval(loadActivities, 60000);
    return () => clearInterval(interval);
  }, [filter, limit]);

  async function loadActivities() {
    try {
      const activities = await api.getActivity(limit, filter);
      setActivities(activities);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load activity:', error);
    } finally {
      setLoading(false);
    }
  }

  function getActivityIcon(type) {
    if (type === 'registration') return '🎉';
    if (type === 'update') return '📈';
    if (type === 'validation') return '🛡️';
    return '📝';
  }

  function timeAgo(timestamp) {
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#3498db]" />
            <h3 className="text-sm font-semibold text-gray-900">Live Activity</h3>
          </div>
          <svg className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-500">{lastRefresh ? `${timeAgo(lastRefresh.toISOString())} ago` : 'Live'}</span>
        </div>
      </div>

      {isExpanded && (
        <>
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-1">
              {['all', 'registration', 'update'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`flex-1 px-2 py-1 rounded text-xs font-medium transition ${filter === type ? 'bg-[#3498db] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  {type === 'all' ? 'All' : type === 'registration' ? 'New' : 'Updates'}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-gray-100 max-h-[calc(100vh-300px)] overflow-y-auto">
            {activities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-xs font-medium">No activity</p>
              </div>
            ) : (
              activities.map((activity, idx) => (
                <div key={idx} onClick={() => onAgentClick && onAgentClick(activity.agent)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition">
                  <div className="flex items-start gap-2">
                    <div className="text-lg flex-shrink-0">{getActivityIcon(activity.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-sm">{activity.agent.name}</span>
                          <span className="text-xs text-gray-500 block">
                            {activity.type === 'registration'
                              ? `registered on ${activity.agent.chain_name}`
                              : activity.type === 'validation'
                                ? `validated: ${activity.data.response}/100`  // shows validations realtime in acivity area
                                : `Rep: ${activity.data.reputation}/100`}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(activity.timestamp)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getChainColor(activity.agent.chain_key) }} />
                        <span className="text-xs text-gray-500 truncate">{activity.agent.chain_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Show:</span>
              <div className="flex gap-1">
                {[10, 25, 50].map((count) => (
                  <button key={count} onClick={() => setLimit(count)} className={`px-2 py-0.5 rounded text-xs font-medium transition ${limit === count ? 'bg-[#3498db] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                    {count}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AgentTable({ agents, onSelect, chains, sortBy, setSortBy, sortOrder, setSortOrder, onSortChange, sorting }) {

  const handleSort = (column) => {
    let newOrder;
    if (sortBy === column) {
      newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      newOrder = 'desc';
    }

    if (onSortChange) {
      onSortChange(column, newOrder);
    } else {
      setSortBy(column);
      setSortOrder(newOrder);
    }
  };

  const SortIcon = ({ column }) => (
    <span className="ml-1 inline-flex flex-col">
      <svg
        className={`w-3 h-3 -mb-1 ${sortBy === column && sortOrder === 'asc' ? 'text-[#3498db]' : 'text-gray-400'}`}
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M12 5l-7 7h14l-7-7z" />
      </svg>
      <svg
        className={`w-3 h-3 ${sortBy === column && sortOrder === 'desc' ? 'text-[#3498db]' : 'text-gray-400'}`}
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M12 19l7-7H5l7 7z" />
      </svg>
    </span>
  );

  return (
    <div className={`overflow-x-auto relative ${sorting ? 'opacity-50 pointer-events-none' : ''}`}>
      {sorting && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <Loader2 className="w-8 h-8 text-[#3498db] animate-spin" />
        </div>
      )}
      <table className="w-full table-fixed">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-64">Agent</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-32">Chain</th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">Owner</th>
            <th
              className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none w-28"
              onClick={() => handleSort('reputation')}
            >
              <div className="flex items-center">
                Reputation
                <SortIcon column="reputation" />
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none w-24"
              onClick={() => handleSort('reviewcount')}
            >
              <div className="flex items-center">
                Feedback
                <SortIcon column="reviewcount" />
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {agents.map((agent) => (
            <tr key={`${agent.chain_key}-${agent.id}`} onClick={() => onSelect(agent)} className="hover:bg-gray-50 cursor-pointer transition">
              <td className="px-6 py-4">
                <Link to={getAgentUrl(agent)} onClick={(e) => e.stopPropagation()} className="flex items-center gap-3 hover:opacity-80">
                  <img
                    src={optimizeImage(agent.image, 112)}
                    alt={agent.name}
                    loading="lazy"
                    decoding="async"
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-lg bg-gray-100"
                    onError={(e) => { e.target.src = `https://api.dicebear.com/9.x/bottts/svg?seed=${agent.chain_key}${agent.id}`; }}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {agent.name.length > 32 ? `${agent.name.slice(0, 32)}...` : agent.name}
                      </span>
                      {agent.verified && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-1">
                      {agent.description?.length > 40 ? `${agent.description.slice(0, 40)}...` : agent.description}
                    </div>
                  </div>
                </Link>
              </td>
              <td className="px-6 py-4"><ChainBadge chainKey={agent.chain_key} chainName={agent.chain_name} /></td>
              <td className="px-6 py-4"><span className="text-sm font-mono text-gray-600">{truncateAddress(agent.owner)}</span></td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5">
                  <Star className={`w-4 h-4 ${agent.reputation >= 0 ? 'text-yellow-500 fill-yellow-500' : 'text-red-500'}`} />
                  <span className="font-semibold text-gray-900">
                    {Math.min(Math.round(agent.reputation), 100)}
                  </span>
                  <span className="text-gray-500 text-sm">/100</span>
                </div>
              </td>
              <td className="px-6 py-4"><span className="text-sm text-gray-900">{agent.reviewCount}</span></td>
              <td className="px-6 py-4">
                {agent.verified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded border border-green-200">
                    <Shield className="w-3 h-3" />Verified
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">Active</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgentCard({ agent, onClick, chains }) {
  return (
    <Link to={getAgentUrl(agent)} className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-[#3498db] hover:shadow-md transition">
      <div className="flex items-start gap-3 mb-3">
        <img
          src={optimizeImage(agent.image, 112)}
          alt={agent.name}
          loading="lazy"
          decoding="async"
          width={56}
          height={56}
          className="w-14 h-14 rounded-lg bg-gray-100"
          onError={(e) => { e.target.src = `https://api.dicebear.com/9.x/bottts/svg?seed=${agent.chain_key}${agent.id}`; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {agent.name.length > 32 ? `${agent.name.slice(0, 32)}...` : agent.name}
            </h3>
            {agent.verified && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
          </div>
          <ChainBadge chainKey={agent.chain_key} chainName={agent.chain_name} />
        </div>
      </div>
      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
        {agent.description?.length > 40 ? `${agent.description.slice(0, 40)}...` : agent.description}
      </p>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          <Star className={`w-4 h-4 ${agent.reputation >= 0 ? 'text-yellow-500 fill-yellow-500' : 'text-red-500'}`} />
          <span className="font-semibold text-gray-900">
            {Math.min(Math.round(agent.reputation), 100)}
          </span>
          <span className="text-gray-500">({agent.reviewCount})</span>
        </div>
        <div className="text-gray-600">{(agent.paidInteractions || 0).toLocaleString('en-US')} uses</div>
      </div>
    </Link>
  );
}

// Add simple Trust Graph Component
function TrustGraphSimple({ agent, chain }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGraph() {
      const data = await api.getTrustGraph(agent.chain_key, agent.id);
      setGraph(data);
      setLoading(false);
    }
    loadGraph();
  }, [agent.chain_key, agent.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!graph || graph.nodes.length <= 1) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <Users className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium">No trust relationships yet</p>
        <p className="text-sm text-gray-500 mt-1">
          This agent hasn't received validations or reviews
        </p>
      </div>
    );
  }

  const validators = graph.nodes.filter(n => n.type === 'validator');
  const clients = graph.nodes.filter(n => n.type === 'client');
  const validationEdges = graph.edges.filter(e => e.type === 'validation');
  const reviewEdges = graph.edges.filter(e => e.type === 'review');

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">Validators</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{validators.length}</div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Reviewers</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{clients.length}</div>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Avg Validation</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{graph.stats.avgValidationScore}/100</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">Avg Review</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{graph.stats.avgReviewScore}/100</div>
        </div>
      </div>

      {/* Visual Graph - Simple CSS Version */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-6 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          Trust Network
        </h3>

        <div className="relative min-h-[300px] flex items-center justify-center">
          {/* Center Agent Node */}
          <div className="absolute z-10 flex flex-col items-center">
            <div className="w-20 h-20 rounded-full border-4 border-blue-500 bg-white shadow-lg overflow-hidden">
              <img
                src={optimizeImage(agent.image, 112)}
                alt={agent.name}
                loading="lazy"
                decoding="async"
                width={56}
                height={56}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = `https://api.dicebear.com/9.x/bottts/svg?seed=${agent.id}`; }}
              />
            </div>
            <div className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
              {agent.name}
            </div>
          </div>

          {/* Validators - Left Side */}
          {validators.length > 0 && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 space-y-3">
              <div className="text-xs font-semibold text-green-600 mb-2 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Validators
              </div>
              {validators.slice(0, 5).map((node, idx) => {
                const edge = validationEdges.find(e => e.source === node.id);
                return (
                  <div key={node.id} className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                      {edge?.score || '?'}
                    </div>
                    <div className="w-16 h-0.5 bg-gradient-to-r from-green-400 to-transparent"></div>
                    <code className="text-xs text-gray-500">{node.label}</code>
                  </div>
                );
              })}
              {validators.length > 5 && (
                <div className="text-xs text-gray-400">+{validators.length - 5} more</div>
              )}
            </div>
          )}

          {/* Reviewers - Right Side */}
          {clients.length > 0 && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 space-y-3 text-right">
              <div className="text-xs font-semibold text-blue-600 mb-2 flex items-center gap-1 justify-end">
                Reviewers <Users className="w-3 h-3" />
              </div>
              {clients.slice(0, 5).map((node, idx) => {
                const edge = reviewEdges.find(e => e.source === node.id);
                return (
                  <div key={node.id} className="flex items-center gap-2 justify-end">
                    <code className="text-xs text-gray-500">{node.label}</code>
                    <div className="w-16 h-0.5 bg-gradient-to-l from-blue-400 to-transparent"></div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold ${(edge?.score || 0) >= 80 ? 'bg-gradient-to-br from-green-400 to-green-500' :
                      (edge?.score || 0) >= 50 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500' :
                        'bg-gradient-to-br from-red-400 to-red-500'
                      }`}>
                      {edge?.score || '?'}
                    </div>
                  </div>
                );
              })}
              {clients.length > 5 && (
                <div className="text-xs text-gray-400">+{clients.length - 5} more</div>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Validation (on-chain proof)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Review (reputation feedback)</span>
          </div>
        </div>
      </div>

      {/* Detailed Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Validators List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-green-50 border-b border-green-200">
            <h4 className="text-sm font-semibold text-green-800 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Validator Trust Sources
            </h4>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {validationEdges.length > 0 ? validationEdges.map((edge, idx) => {
              const node = validators.find(n => n.id === edge.source);
              return (
                <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <code className="text-sm font-mono text-gray-700">{node?.address || edge.source}</code>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {edge.timestamp ? new Date(edge.timestamp).toLocaleDateString() : 'Unknown date'}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${edge.score >= 80 ? 'bg-green-100 text-green-700' :
                    edge.score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                    {edge.score}/100
                  </div>
                </div>
              );
            }) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">No validators yet</div>
            )}
          </div>
        </div>

        {/* Reviewers List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
            <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <Users className="w-4 h-4" /> Client Trust Sources
            </h4>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {reviewEdges.length > 0 ? reviewEdges.map((edge, idx) => {
              const node = clients.find(n => n.id === edge.source);
              return (
                <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <code className="text-sm font-mono text-gray-700">{node?.address || edge.source}</code>
                    {edge.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {edge.tags.map((tag, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${edge.score >= 80 ? 'bg-green-100 text-green-700' :
                    edge.score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                    {edge.score}/100
                  </div>
                </div>
              );
            }) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">No reviews yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile Expandable Component for Agent Name and Description
function MobileExpandable({ name, description, verified }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const textRef = useRef(null);

  // Check if text is truncated
  useEffect(() => {
    if (textRef.current) {
      const isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
      setNeedsExpansion(isOverflowing || name.length > 25);
    }
  }, [name, description]);

  return (
    <div className="sm:hidden mb-2">
      {/* Name row/ */}
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h1 className={`text-lg font-bold text-gray-900 ${!isExpanded ? 'truncate max-w-[180px]' : ''}`}>
          {name.length > 32 && !isExpanded ? `${name.slice(0, 32)}...` : name}
        </h1>
        {verified && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-200 font-medium flex-shrink-0">
            <Shield className="w-3 h-3" />Verified
          </span>
        )}
      </div>

      {/* Description */}
      <p
        ref={textRef}
        className={`text-xs text-gray-600 ${!isExpanded ? 'line-clamp-2' : ''}`}
      >
        {description}
      </p>

      {/* Expand/Collapse button */}
      {(needsExpansion || description?.length > 80) && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-[10px] text-blue-600 font-medium flex items-center gap-0.5"
        >
          {isExpanded ? (
            <>
              Show less
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </>
          ) : (
            <>
              Show more
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
}

function AgentDetail({ agent, chains, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const chain = chains.find(c => c.key === agent.chain_key);
  const location = useLocation();
  const navigate = useNavigate();

  // Sync tab with URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (['overview', 'reputation', 'validations', 'metadata'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`${location.pathname}#${tab}`, { replace: true });
  };

  return (
    <div className="space-y-2">
      {/* Compact Navigation Bar */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/" className="text-[#3498db] hover:text-[#2980b9] font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Explorer
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">{agent.chain_name}</span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{agent.name}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigator.clipboard.writeText(window.location.href)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            title="Copy link"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Agent Header Card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <img
              src={optimizeImage(agent.image, 112)}
              alt={agent.name}
              loading="lazy"
              decoding="async"
              width={56}
              height={56}
              className="w-16 h-16 sm:w-32 sm:h-32 rounded-xl bg-gray-100 border-2 border-gray-200 flex-shrink-0"
              onError={(e) => { e.target.src = `https://api.dicebear.com/9.x/bottts/svg?seed=${agent.chain_key}${agent.id}`; }}
            />

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className="min-w-0 w-full">
                  {/* Mobile: Expandable name/description */}
                  <MobileExpandable
                    name={agent.name}
                    description={agent.description}
                    verified={agent.verified}
                  />

                  {/* Desktop: Always show full content */}
                  <div className="hidden sm:block">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {agent.name.length > 32 ? `${agent.name.slice(0, 32)}...` : agent.name}
                      </h1>
                      {agent.verified && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full border border-green-200 font-medium flex-shrink-0">
                          <Shield className="w-4 h-4" />Verified
                        </span>
                      )}
                    </div>
                    <p className="text-base text-gray-600 mb-3">{agent.description}</p>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
                    <ChainBadge chainKey={agent.chain_key} chainName={agent.chain_name} />
                    <span className="text-[10px] sm:text-xs text-gray-400">(ID: {agent.chain_id})</span>
                    <span className="text-[10px] sm:text-sm text-gray-500">Agent #{agent.id}</span>
                  </div>
                </div>
              </div>

              {/* Stats - Stack on mobile, inline on desktop */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-6 mt-2 sm:mt-4">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Star className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-yellow-500" />
                  <span className="text-[10px] sm:text-sm text-gray-600">Reputation:</span>
                  <span className="text-sm sm:text-lg font-bold text-gray-900">{Math.min(agent.reputation, 100)}</span>
                  <span className="text-[10px] sm:text-sm text-gray-500">/100</span>
                </div>

                <div className="hidden sm:block w-px h-6 bg-gray-300"></div>

                <div className="flex items-center gap-1.5 sm:gap-2">
                  <MessageSquare className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-blue-500" />
                  <span className="text-[10px] sm:text-sm text-gray-600">Feedback:</span>
                  <span className="text-sm sm:text-lg font-bold text-gray-900">{agent.feedback_count || agent.reviewCount || 0}</span>
                </div>

                <div className="hidden sm:block w-px h-6 bg-gray-300"></div>

                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Shield className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-green-500" />
                  <span className="text-[10px] sm:text-sm text-gray-600">Validations:</span>
                  <span className="text-sm sm:text-lg font-bold text-gray-900">{agent.validation_count || (agent.validations || []).length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            {['overview', 'trust', 'reputation', 'validations'].map(tab => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-semibold transition border-b-2 ${activeTab === tab ? 'text-[#3498db] border-[#3498db] bg-blue-50' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
            {/* Metadata tab - hidden on mobile */}
            <button
              onClick={() => handleTabChange('metadata')}
              className={`hidden sm:block px-6 py-4 text-sm font-semibold transition border-b-2 ${activeTab === 'metadata' ? 'text-[#3498db] border-[#3498db] bg-blue-50' : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'}`}
            >
              Metadata
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <AgentOverviewTab agent={agent} chain={chain} />
          )}
          {activeTab === 'trust' && (
            <TrustGraphSimple agent={agent} chain={chain} />
          )}
          {activeTab === 'reputation' && (
            <AgentReputationTab agent={agent} />
          )}
          {activeTab === 'validations' && (
            <AgentValidationsTab agent={agent} />
          )}
          {activeTab === 'metadata' && (
            <AgentMetadataTab agent={agent} />
          )}
        </div>
      </div>
    </div>
  );
}



/**
 * UPDATED AGENT OVERVIEW TAB
 * 
 * Layout:
 * - Left 2/3: All the detail cards stacked vertically
 * - Right 1/3: On-Chain Metadata + Quick Stats (sticky)
 * 
 * Replace your existing AgentOverviewTab function with this one
 */

function AgentOverviewTab({ agent, chain }) {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const decodeHexIfNeeded = (value) => {
    if (typeof value === 'string' && value.startsWith('0x')) {
      try {
        // Remove 0x and convert hex to string
        const hex = value.slice(2);
        let str = '';
        for (let i = 0; i < hex.length; i += 2) {
          str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
        }
        // Return decoded if it's readable, otherwise return original
        if (/^[\x20-\x7E\s]+$/.test(str)) {
          return str;
        }
      } catch {
        // If decode fails, return original
      }
    }
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateString = (str, maxLength = 20) => {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(timestamp);
  };

  const getExplorerUrl = (address, type = 'address') => {
    if (!chain) return '#';
    return `${chain.explorer}/${type}/${address}`;
  };

  // Parse endpoints - handle both object and array formats
  const parseEndpoints = () => {
    const endpoints = agent.services || agent.endpoints || {};
    const parsed = [];

    if (Array.isArray(endpoints)) {
      endpoints.forEach((ep, idx) => {
        if (typeof ep === 'object') {
          parsed.push({
            name: ep.name || `Endpoint ${idx + 1}`,
            version: ep.version || '',
            url: ep.endpoint || ep.url || '',
            description: ep.description || ''
          });
        } else if (typeof ep === 'string') {
          parsed.push({ name: `Endpoint ${idx + 1}`, version: '', url: ep, description: '' });
        }
      });
    } else if (typeof endpoints === 'object') {
      Object.entries(endpoints).forEach(([key, value]) => {
        if (typeof value === 'object') {
          parsed.push({
            name: value.name || key,
            version: value.version || '',
            url: value.endpoint || value.url || '',
            description: value.description || ''
          });
        } else if (typeof value === 'string') {
          parsed.push({ name: key, version: '', url: value, description: '' });
        }
      });
    }

    return parsed;
  };

  const parsedEndpoints = parseEndpoints();
  const validations = agent.validations || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Left Column - 2/3 width */}
      <div className="lg:col-span-2 space-y-6">

        {/* Identity Registry */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Identity Registry</h3>
              </div>
              {chain?.contracts && (

                <a href={getExplorerUrl(chain.contracts.identity)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  View Contract <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Owner Address</label>
                <div className="flex items-center gap-2 group">
                  <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate border border-gray-200">
                    {agent.owner}
                  </code>
                  <button
                    onClick={() => copyToClipboard(agent.owner, 'owner')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                  >
                    {copied === 'owner' ? <CheckCircle className="w-4 h-4 text-green-500" /> : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                  <a href={getExplorerUrl(agent.owner)} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-600">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              {chain?.contracts && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Identity Contract</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate border border-gray-200">
                      {chain.contracts.identity}
                    </code>
                    <button
                      onClick={() => copyToClipboard(chain.contracts.identity, 'contract')}
                      className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                    >
                      {copied === 'contract' ? <CheckCircle className="w-4 h-4 text-green-500" /> : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                    <a href={getExplorerUrl(chain.contracts.identity)} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-600">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ========== INSERT AGENT WALLET SECTION HERE ========== */}
        {agent.agentWallet && agent.agentWallet !== '0x0000000000000000000000000000000000000000' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-yellow-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Agent Wallet</h3>
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">EIP-712 Verified</span>
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs text-gray-500 mb-3">
                This is the verified autonomous wallet for this agent, signed with EIP-712.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate border border-gray-200">
                  {agent.agentWallet}
                </code>
                <button
                  onClick={() => copyToClipboard(agent.agentWallet, 'agent-wallet')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                >
                  {copied === 'agent-wallet' ? <CheckCircle className="w-4 h-4 text-green-500" /> : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
                <a href={getExplorerUrl(agent.agentWallet)} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-600">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Reputation Registry */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-orange-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Reputation Registry</h3>
              </div>
              {chain?.contracts && (
                <a href={getExplorerUrl(chain.contracts.reputation)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  View Contract <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle cx="40" cy="40" r="36" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                  <circle cx="40" cy="40" r="36" stroke={Math.min(agent.reputation, 100) >= 80 ? '#22c55e' : Math.min(agent.reputation, 100) >= 50 ? '#eab308' : '#ef4444'} strokeWidth="8" fill="none" strokeDasharray={`${(Math.min(agent.reputation, 100) / 100) * 226} 226`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-gray-900">{Math.min(agent.reputation, 100)}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-1">Overall Score</div>
                <div className="text-xs text-gray-500">Based on {agent.reviewCount || 0} feedback{(agent.reviewCount || 0) !== 1 ? 's' : ''}</div>
                {agent.reputation >= 80 && <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded"><TrendingUp className="w-3 h-3" /> Excellent</span>}
                {agent.reputation >= 50 && agent.reputation < 80 && <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-yellow-50 text-yellow-700 text-xs font-medium rounded">Good</span>}
                {agent.reputation < 50 && agent.reputation > 0 && <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded">Needs Improvement</span>}
              </div>
            </div>
            {chain?.contracts && (
              <div className="pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500 mb-2">Reputation Contract</div>
                <code className="text-xs font-mono text-gray-600 break-all">{chain.contracts.reputation}</code>
              </div>
            )}
          </div>
        </div>

        {/* Validation Registry */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Validation Registry</h3>
              </div>
              {chain?.contracts && (
                <a href={getExplorerUrl(chain.contracts.validation)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  View Contract <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-4 rounded-full ${agent.verified ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Shield className={`w-8 h-8 ${agent.verified ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-gray-900">{agent.verified ? 'Validated Agent' : 'Not Validated'}</div>
                <div className="text-sm text-gray-500">{agent.verified ? 'This agent has been verified by on-chain validators' : 'No validations recorded for this agent'}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Validation Count</div>
                <div className="text-lg font-bold text-gray-900">{agent.validation_count || validations.length || (agent.verified ? '1+' : '0')}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <div className={`text-lg font-bold ${agent.verified ? 'text-green-600' : 'text-gray-400'}`}>{agent.verified ? 'Active' : 'Pending'}</div>
              </div>
            </div>
            {chain?.contracts && (
              <div className="pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500 mb-2">Validation Contract</div>
                <code className="text-xs font-mono text-gray-600 break-all">{chain.contracts.validation}</code>
              </div>
            )}
          </div>
        </div>

        {/* Agent Endpoints */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-blue-50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Agent Endpoints</h3>
              <span className="ml-2 px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-medium rounded">{parsedEndpoints.length} configured</span>
            </div>
          </div>
          <div className="p-5">
            {parsedEndpoints.length > 0 ? (
              <div className="space-y-3">
                {parsedEndpoints.map((ep, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{ep.name}</span>
                        {ep.version && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">v{ep.version}</span>}
                      </div>
                      {ep.url && (
                        <code className="text-xs font-mono text-gray-600 truncate block">{ep.url}</code>
                      )}
                      {ep.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{ep.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {ep.url && (
                        <>
                          <button
                            onClick={() => copyToClipboard(ep.url, `endpoint-${idx}`)}
                            className="p-1.5 hover:bg-gray-200 rounded transition text-gray-500 hover:text-gray-700"
                          >
                            {copied === `endpoint-${idx}` ? <CheckCircle className="w-4 h-4 text-green-500" /> : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                          {typeof ep.url === 'string' && ep.url.startsWith('http') && (
                            <a href={ep.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-gray-200 rounded transition text-gray-500 hover:text-blue-600">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No endpoints configured</p>
              </div>
            )}
          </div>
        </div>

        {/* Capabilities + Tags */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Capabilities */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Capabilities</h3>
                <span className="ml-2 px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded">{(agent.capabilities || []).length}</span>
              </div>
            </div>
            <div className="p-5">
              {(agent.capabilities || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(agent.capabilities || []).map((cap, idx) => (
                    <div key={idx} className="inline-flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg border border-violet-200">
                      <CheckCircle className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-violet-800">{cap}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No capabilities listed</p>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-pink-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Tags</h3>
                <span className="ml-2 px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-medium rounded">{(agent.tags || []).length}</span>
              </div>
            </div>
            <div className="p-5">
              {(agent.tags || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(agent.tags || []).map((tag, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-rose-50 text-rose-700 rounded-full text-sm font-medium border border-rose-200">#{tag}</span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No tags assigned</p>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {agent.description && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Description</h3>
              </div>
            </div>
            <div className="p-5">
              <p className="text-gray-700 leading-relaxed">{agent.description}</p>
            </div>
          </div>
        )}

        {/* Recent Validations */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Recent Validations</h3>
                <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">{validations.length || 0}</span>
              </div>
            </div>
          </div>
          <div className="p-5">
            {validations.length > 0 ? (
              <div className="space-y-3">
                {validations.map((validation, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <a href={getExplorerUrl(validation.validator)} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-gray-900 hover:text-blue-600">
                          {validation.validator ? `${validation.validator.slice(0, 6)}...${validation.validator.slice(-4)}` : 'Unknown'}
                        </a>
                        <div className="text-xs text-gray-500">{timeAgo(validation.timestamp)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {validation.tag && <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">{validation.tag}</span>}
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${validation.response >= 80 ? 'bg-green-500' : validation.response >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${validation.response || 0}%` }} />
                        </div>
                        <span className="text-sm font-bold text-gray-900 w-6">{validation.response || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No validations yet</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Right Column - 1/3 width (sticky sidebar) */}
      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-6">

          {/* On-Chain Metadata Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">On-Chain Metadata</h3>
              </div>
            </div>
            <div className="p-4 space-y-1">

              {/* Agent Registry - ADD THIS */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-xs font-medium text-gray-500 shrink-0 mr-2">Agent Registry</span>
                <div className="flex items-center gap-1 min-w-0">
                  <span
                    className="text-xs font-mono text-gray-700 truncate max-w-[140px]"
                    title={`eip155:${agent.chain_id}:${chain?.contracts?.identity || ''}`}
                  >
                    eip155:{agent.chain_id}:{chain?.contracts?.identity ? `${chain.contracts.identity.slice(0, 6)}...${chain.contracts.identity.slice(-4)}` : ''}
                  </span>
                  <button
                    onClick={() => copyToClipboard(`eip155:${agent.chain_id}:${chain?.contracts?.identity || ''}`, 'registry')}
                    className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 shrink-0"
                  >
                    {copied === 'registry' ? <CheckCircle className="w-3 h-3 text-green-500" /> : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Dynamically render ALL metadata fields */}
              {agent.metadata_json && Object.entries(agent.metadata_json)
                .filter(([key]) => !['name', 'description', 'image', 'external_url', 'type', 'endpoints', 'registrations'].includes(key))
                .map(([key, value]) => {
                  const displayKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                  // Arrays get badge treatment on the right
                  if (Array.isArray(value)) {
                    return (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                        <span className="text-xs font-medium text-gray-500 shrink-0 mr-2">{displayKey}</span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {value.map((item, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200">
                              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  const valueStr = typeof value === 'object'
                    ? JSON.stringify(value)
                    : decodeHexIfNeeded(String(value));

                  return (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <span className="text-xs font-medium text-gray-500 shrink-0 mr-2">{displayKey}</span>
                      <div className="flex items-center gap-1 min-w-0">
                        <span
                          className="text-xs font-mono text-gray-700 truncate max-w-[140px]"
                          title={valueStr}
                        >
                          {valueStr}
                        </span>
                        <button
                          onClick={() => copyToClipboard(valueStr, `meta-${key}`)}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 shrink-0"
                        >
                          {copied === `meta-${key}` ? <CheckCircle className="w-3 h-3 text-green-500" /> : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* Fallback: If no metadata_json, show message */}
              {(!agent.metadata_json || Object.keys(agent.metadata_json).filter(k => !['name', 'description', 'image', 'external_url', 'type', 'endpoints', 'registrations'].includes(k)).length === 0) && (
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No additional metadata available</p>
                  <p className="text-xs text-gray-400 mt-1">This agent may not have custom metadata fields</p>
                </div>
              )}

            </div>
          </div>

          {/* Quick Stats Card - System Info */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Quick Stats</h3>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Agent ID */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Agent ID</span>
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold text-gray-900">#{agent.id}</span>
                  <button onClick={() => copyToClipboard(agent.id.toString(), 'agent-id')} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700">
                    {copied === 'agent-id' ? <CheckCircle className="w-4 h-4 text-green-500" /> : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Agent URI */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Agent URI</span>

                <div className="flex items-center gap-2 max-w-[60%]">
                  <span className="text-sm text-gray-900 truncate" title={agent.token_uri}>
                    {agent.token_uri || 'N/A'}
                  </span>
                  <button
                    onClick={() => copyToClipboard(agent.token_uri, 'agent-uri')}
                    className="p-1 hover:bg-gray-100 rounded transition text-gray-500 hover:text-gray-700"
                  >
                    {copied === 'agent-uri' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-teal-500" />
                  <span className="text-sm text-gray-600">Status</span>
                </div>
                {agent.verified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200">
                    <CheckCircle className="w-3 h-3" />Verified
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Unverified</span>
                )}
              </div>

              {/* Created */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-xs text-gray-700">{formatDate(agent.created_at)}</span>
              </div>

              {/* Last Updated */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Last Updated</span>
                <span className="text-xs text-gray-700">{formatDate(agent.last_updated)}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Raw Data - Mobile Only */}
      <div className="lg:hidden col-span-full bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Raw Data</h3>
          </div>
        </div>
        <div className="p-4">
          <div className="px-3 py-3 bg-gray-900 rounded-lg border border-gray-700 overflow-auto max-h-64">
            <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(agent.metadata_json, null, 2)}
            </pre>
          </div>
        </div>
      </div>

    </div >
  );
}

function AgentReputationTab({ agent, chain }) {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExplorerUrl = (address, type = 'address') => {
    if (!chain) return '#';
    return `${chain.explorer}/${type}/${address}`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    if (score >= 40) return 'orange';
    return 'red';
  };

  const getScoreLabel = (score, reviewCount) => {
    if (reviewCount === 0) return 'No Feedback Yet';
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Above Average';
    if (score >= 50) return 'Average';
    if (score >= 40) return 'Below Average';
    if (score >= 30) return 'Poor';
    return 'Very Poor';
  };

  const feedback = agent.feedback || [];

  // Calculate stats
  const avgScore = Math.min(agent.reputation || 0, 100);
  const totalReviews = agent.feedback_count || agent.reviewCount || feedback.length;
  const scoreDistribution = {
    excellent: feedback.filter(f => f.score >= 80).length,
    good: feedback.filter(f => f.score >= 60 && f.score < 80).length,
    average: feedback.filter(f => f.score >= 40 && f.score < 60).length,
    poor: feedback.filter(f => f.score < 40).length,
  };

  return (
    <div className="space-y-6">
      {/* Reputation Summary */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Score Circle */}
          <div className="relative w-32 h-32">
            <svg className="w-32 h-32 transform -rotate-90">
              <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="12" fill="none" />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke={avgScore >= 80 ? '#22c55e' : avgScore >= 50 ? '#eab308' : '#ef4444'}
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${(avgScore / 100) * 352} 352`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-gray-900">{avgScore}</span>
              <span className="text-xs text-gray-500">/100</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex-1 text-center md:text-left">
            <div className="text-sm font-semibold text-yellow-800 uppercase mb-1">Overall Reputation</div>
            <div className="text-2xl font-bold text-gray-900 mb-2">{getScoreLabel(avgScore, totalReviews)}</div>
            <div className="text-sm text-gray-600">
              Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''} from clients
            </div>

            {/* Mini distribution */}
            {feedback.length > 0 && (
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-600">{scoreDistribution.excellent} excellent</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-xs text-gray-600">{scoreDistribution.good} good</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-xs text-gray-600">{scoreDistribution.average} avg</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs text-gray-600">{scoreDistribution.poor} poor</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-white rounded-lg border border-yellow-200 text-center">
              <div className="text-2xl font-bold text-gray-900">{totalReviews}</div>
              <div className="text-xs text-gray-500">Total Reviews</div>
            </div>
            <div className="p-3 bg-white rounded-lg border border-yellow-200 text-center">
              <div className="text-2xl font-bold text-gray-900">{avgScore}</div>
              <div className="text-xs text-gray-500">Avg Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Reputation Contract Info */}
      {chain?.contracts && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Reputation Contract</h3>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate border border-gray-200">
                {chain.contracts.reputation}
              </code>
              <button
                onClick={() => copyToClipboard(chain.contracts.reputation, 'reputation-contract')}
                className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                title="Copy address"
              >
                {copied === 'reputation-contract' ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              <a
                href={getExplorerUrl(chain.contracts.reputation)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-600"
                title="View on explorer"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Feedback History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-amber-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Feedback History</h3>
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                {feedback.length === 50 && (agent.feedback_count || 0) > 50
                  ? `Latest 50 of ${agent.feedback_count}`
                  : `${agent.feedback_count || feedback.length} record${(agent.feedback_count || feedback.length) !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        </div>
        <div className="p-5">
          {feedback.length > 0 ? (
            <div className="space-y-4">
              {feedback.map((review, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border transition ${review.isRevoked
                    ? 'bg-red-50 border-red-200 opacity-70'
                    : 'bg-gray-50 border-gray-200 hover:border-yellow-300'
                    }`}
                >
                  {/* Revoked badge */}
                  {review.isRevoked && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded border border-red-200">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Revoked
                      </span>
                      <span className="text-xs text-red-600">This feedback has been revoked by the client</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${review.score >= 80 ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                        review.score >= 60 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                          review.score >= 40 ? 'bg-gradient-to-br from-orange-400 to-red-400' :
                            'bg-gradient-to-br from-red-400 to-red-600'
                        }`}>
                        <Star className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Client</div>
                        <a
                          href={getExplorerUrl(review.client)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm text-gray-900 hover:text-blue-600 transition flex items-center gap-1"
                        >
                          {review.client ? `${review.client.slice(0, 6)}...${review.client.slice(-4)}` : 'Unknown'}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${review.value !== undefined
                        ? (review.value < 0 ? 'bg-red-100 text-red-700' :
                          review.value >= 80 ? 'bg-green-100 text-green-700' :
                            review.value >= 60 ? 'bg-yellow-100 text-yellow-700' :
                              review.value >= 40 ? 'bg-orange-100 text-orange-700' :
                                'bg-red-100 text-red-700')
                        : (review.score >= 80 ? 'bg-green-100 text-green-700' :
                          review.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            review.score >= 40 ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700')
                        }`}>
                        <Star className="w-4 h-4" />
                        {review.value !== undefined
                          ? (review.valueDecimals > 0
                            ? review.value.toFixed(Math.min(review.valueDecimals, 2))
                            : review.value)
                          : review.score}
                        {(review.value === undefined || (review.value >= 0 && review.value <= 100)) && '/100'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
                    {/* Feedback Index - NEW */}
                    {review.feedbackIndex && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Feedback #</div>
                        <div className="text-sm font-medium text-gray-700">{review.feedbackIndex}</div>
                      </div>
                    )}

                    {/* Score Bar */}
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500 mb-1">Score</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${review.score >= 80 ? 'bg-green-500' :
                              review.score >= 60 ? 'bg-yellow-500' :
                                review.score >= 40 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                            style={{ width: `${review.score || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-900 min-w-[3rem]">{review.score}/100</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Tags</div>
                      <div className="flex flex-wrap gap-1">
                        {review.tag1 && (
                          <span className="inline-flex px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200">
                            {review.tag1}
                          </span>
                        )}
                        {review.tag2 && (
                          <span className="inline-flex px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded border border-purple-200">
                            {review.tag2}
                          </span>
                        )}
                        {!review.tag1 && !review.tag2 && (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </div>

                    {/* Time */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Time</div>
                      <div className="text-sm text-gray-700">{timeAgo(review.timestamp)}</div>
                    </div>
                  </div>

                  {/* Full Client Address */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Full Client Address</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-600 truncate">
                        {review.client}
                      </code>
                      <button
                        onClick={() => copyToClipboard(review.client, `client-${idx}`)}
                        className="p-1 hover:bg-gray-200 rounded transition text-gray-500 hover:text-gray-700"
                      >
                        {copied === `client-${idx}` ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 bg-yellow-50 rounded-full flex items-center justify-center">
                <Star className="w-10 h-10 text-yellow-400" />
              </div>
              <p className="text-lg text-gray-700 font-medium">No Reviews Yet</p>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                This agent has not received any feedback from clients. Reviews are submitted through the Reputation Registry contract.
              </p>
              {chain?.contracts && (
                <a
                  href={getExplorerUrl(chain.contracts.reputation)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition text-sm font-medium"
                >
                  View Reputation Contract
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentValidationsTab({ agent, chain }) {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExplorerUrl = (address, type = 'address') => {
    if (!chain) return '#';
    return `${chain.explorer}/${type}/${address}`;
  };

  const validations = agent.validations || [];

  return (
    <div className="space-y-6">
      {/* Validation Status Summary */}
      {agent.verified ? (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-green-500 rounded-full">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-green-800 uppercase mb-1">Validation Status</div>
              <div className="text-2xl font-bold text-green-900">Verified Agent</div>
              <div className="text-sm text-green-700 mt-1">
                This agent has {agent.validation_count || validations.length} validation{(agent.validation_count || validations.length) !== 1 ? 's' : ''} on-chain
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-green-600">{agent.validation_count || validations.length}</div>
              <div className="text-sm text-green-700">Total Validations</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-gray-300 rounded-full">
              <Shield className="w-10 h-10 text-gray-500" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-500 uppercase mb-1">Validation Status</div>
              <div className="text-2xl font-bold text-gray-700">Not Validated</div>
              <div className="text-sm text-gray-500 mt-1">
                This agent has not received any validator checks yet
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Contract Info */}
      {chain?.contracts && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Validation Contract</h3>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate border border-gray-200">
                {chain.contracts.validation}
              </code>
              <button
                onClick={() => copyToClipboard(chain.contracts.validation, 'validation-contract')}
                className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                title="Copy address"
              >
                {copied === 'validation-contract' ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              <a
                href={getExplorerUrl(chain.contracts.validation)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-600"
                title="View on explorer"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Validation History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Validation History</h3>
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                {validations.length === 50 && (agent.validation_count || 0) > 50
                  ? `Latest 50 of ${agent.validation_count}`
                  : `${agent.validation_count || validations.length} record${(agent.validation_count || validations.length) !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        </div>
        <div className="p-5">
          {validations.length > 0 ? (
            <div className="space-y-4">
              {validations.map((validation, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-green-300 transition"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Validator</div>
                        <a
                          href={getExplorerUrl(validation.validator)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm text-gray-900 hover:text-blue-600 transition flex items-center gap-1"
                        >
                          {validation.validator}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${validation.status === 'COMPLETED' || validation.response > 0
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                        }`}>
                        {validation.status === 'COMPLETED' || validation.response > 0 ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        )}
                        {validation.status || (validation.response > 0 ? 'COMPLETED' : 'PENDING')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">

                    {/* Response Score */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Response Score</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${validation.response >= 80 ? 'bg-green-500' :
                              validation.response >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                            style={{ width: `${validation.response || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-900 min-w-[2rem]">{validation.response || 0}</span>
                      </div>
                    </div>

                    {/* Tag */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Tag</div>
                      {validation.tag ? (
                        <span className="inline-flex px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200">
                          {validation.tag}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </div>

                    {/* Time */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Time</div>
                      <div className="text-sm text-gray-700">{timeAgo(validation.timestamp)}</div>
                    </div>

                    {/* Full Timestamp */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Timestamp</div>
                      <div className="text-sm text-gray-700">{formatDate(validation.timestamp)}</div>
                    </div>
                  </div>

                  {/* Hash */}
                  {validation.hash && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500 mb-1">Request Hash</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-600 truncate">
                          {validation.hash}
                        </code>
                        <button
                          onClick={() => copyToClipboard(validation.hash, `hash-${idx}`)}
                          className="p-1 hover:bg-gray-200 rounded transition text-gray-500 hover:text-gray-700"
                        >
                          {copied === `hash-${idx}` ? (
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Shield className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-lg text-gray-700 font-medium">No Validations Yet</p>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                This agent has not received any validator checks. Validators can submit verification requests through the Validation Registry contract.
              </p>
              {chain?.contracts && (
                <a
                  href={getExplorerUrl(chain.contracts.validation)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                >
                  View Validation Contract
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentMetadataTab({ agent }) {
  const [copied, setCopied] = useState(false);

  const getOrderedMetadata = () => {
    if (!agent.metadata_json) return null;

    const m = agent.metadata_json;
    const ordered = {};

    // Spec order: type, name, description, image, endpoints, x402Support, active, registrations, supportedTrust
    if (m.type !== undefined) ordered.type = m.type;
    if (m.name !== undefined) ordered.name = m.name;
    if (m.description !== undefined) ordered.description = m.description;
    if (m.image !== undefined) ordered.image = m.image;
    if (m.endpoints !== undefined) ordered.endpoints = m.endpoints;
    if (m.x402Support !== undefined) ordered.x402Support = m.x402Support;
    if (m.x402support !== undefined) ordered.x402Support = m.x402support; // handle lowercase
    if (m.active !== undefined) ordered.active = m.active;
    if (m.registrations !== undefined) ordered.registrations = m.registrations;
    if (m.supportedTrust !== undefined) ordered.supportedTrust = m.supportedTrust;
    if (m.supportedTrusts !== undefined) ordered.supportedTrusts = m.supportedTrusts; // handle plural variant

    // Append any extra fields not in spec
    const specKeys = ['type', 'name', 'description', 'image', 'endpoints', 'x402Support', 'x402support', 'active', 'registrations', 'supportedTrust', 'supportedTrusts'];
    Object.keys(m).forEach(key => {
      if (!specKeys.includes(key)) {
        ordered[key] = m[key];
      }
    });

    return ordered;
  };

  const orderedMetadata = getOrderedMetadata();
  const jsonString = orderedMetadata ? JSON.stringify(orderedMetadata, null, 2) : 'No metadata available';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-1 h-4 bg-[#3498db] rounded"></div>
          Owner Address
        </h3>
        <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="font-mono text-sm text-gray-900 break-all">{agent.owner}</div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-1 h-4 bg-[#3498db] rounded"></div>
          Last Updated
        </h3>
        <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-900">{new Date(agent.last_updated).toLocaleString()}</div>
        </div>
      </div>

      {agent.created_at && (
        <div>
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
            <div className="w-1 h-4 bg-[#3498db] rounded"></div>
            Created At
          </h3>
          <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-900">{new Date(agent.created_at).toLocaleString()}</div>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-1 h-4 bg-[#3498db] rounded"></div>
          Raw Data
        </h3>
        <div className="relative">
          <button
            onClick={copyToClipboard}
            className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition text-gray-400 hover:text-white z-10"
            title="Copy JSON"
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <div className="px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 overflow-auto max-h-96">
            <pre className="text-xs text-green-400 font-mono">
              {jsonString}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer({ stats, agents }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetchWithAuth(`${BACKEND_API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'You\'re in! Welcome to the agent economy.' });
        setEmail('');
        setName('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to subscribe. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-16 bg-gradient-to-b from-gray-50 to-gray-100 border-t border-gray-200">
      {/* Email Capture Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* CTA Text */}
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                Stay ahead of the agent economy
              </h3>
              <p className="text-blue-100 text-sm max-w-md">
                Get weekly updates on new agents, protocol upgrades, and the future of decentralized AI.
              </p>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubscribe} className="w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent w-full sm:w-40"
                />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent w-full sm:w-64"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Subscribing...
                    </span>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>

              {/* Message */}
              {message && (
                <div className={`mt-3 text-sm ${message.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                  {message.text}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">ERC-8004</h3>
            <ul className="space-y-3">
              <li><a href="https://eips.ethereum.org/EIPS/eip-8004" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">EIP-8004 Specification <ExternalLink className="w-3 h-3" /></a></li>
              <li><a href="https://github.com/erc-8004" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">GitHub Repository <ExternalLink className="w-3 h-3" /></a></li>
              <li><a href="https://8004.org" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">8004.org <ExternalLink className="w-3 h-3" /></a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Community</h3>
            <ul className="space-y-3">
              <li><a href="https://t.me/ERC8004" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">Telegram <ExternalLink className="w-3 h-3" /></a></li>
              <li><a href="https://ethereum-magicians.org/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">Forum <ExternalLink className="w-3 h-3" /></a></li>
              <li><a href="https://x.com/AINewsOfficial_" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-[#3498db] transition flex items-center gap-2">X <ExternalLink className="w-3 h-3" /></a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Contact</h3>
            <div className="space-y-2">
              <a
                href="#"
                className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = 'mailto:' + 'service' + '@' + '8004agents' + '.ai';
                }}
              >
                service@8004agents.ai
              </a>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 mt-6">Stats</h3>
            </div>
            {stats && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Indexed Agents:</span>
                  <span className="font-semibold text-gray-900">{stats.total?.inDatabase?.toLocaleString('en-US') || '0'}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Supported Chains:</span>
                  <span className="font-semibold text-gray-900">{Object.keys(stats.chains || {}).length}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Total Reviews:</span>
                  <span className="font-semibold text-gray-900">{agents.reduce((sum, a) => sum + (a.reviewCount || 0), 0).toLocaleString('en-US')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="pt-8 border-t border-gray-300">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#3498db] to-[#2980b9] rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-900">ERC-8004 Explorer & News</div>
                <div className="text-xs text-gray-500">Powered by ERC-8004 Standard</div>
              </div>
            </Link>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-700 transition">
                Privacy
              </Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700 transition">
                Terms
              </Link>
              <span className="text-sm text-gray-400">© 2025-2026 8004agents.ai</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default App;